(function initNuSpecFilters() {
  // Keep Storefront API on a supported baseline to avoid old-version behavior drift.
  const DEFAULT_API_VERSION = '2026-04';
  const MIN_SUPPORTED_API_VERSION = '2026-04';
  const PRODUCTS_PAGE_SIZE = 250;
  const MAX_PAGE_FETCH = 20;
  // Caps concurrent Section Rendering requests when fetching every page of a large
  // collection — matches the number of same-origin connections most browsers keep open
  // per host, so this is roughly the most that could ever actually be in flight at once.
  const SECTION_FETCH_CONCURRENCY = 6;
  const MOBILE_FILTER_QUERY = '(max-width: 768.98px)';
  const PRODUCT_MAP_CACHE_TTL_MS = 5 * 60 * 1000;
  const nuFiltersLite = window.NuFiltersLite || null;

  function scheduleIdle(fn, timeout = 2000) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout });
    } else {
      window.setTimeout(fn, 0);
    }
  }

  function isNuSpecDebugEnabled() {
    try {
      const debugParam = nuFiltersLite?.getQueryParam
        ? nuFiltersLite.getQueryParam('nu_spec_debug')
        : new URLSearchParams(window.location.search).get('nu_spec_debug');
      if (debugParam === '1') return true;
      if (window.localStorage.getItem('nu_spec_debug') === '1') return true;
    } catch (error) {
      return false;
    }
    return false;
  }

  function nuSpecDebug(enabled, label, payload = null) {
    if (!enabled) return;
    if (payload === null) {
      console.log(`[nu-spec-debug] ${label}`); // eslint-disable-line no-console
    } else {
      console.log(`[nu-spec-debug] ${label}`, payload); // eslint-disable-line no-console
    }
  }

  function summarizeProductMap(productMap, filterKeys = []) {
    const summary = {
      products: Object.keys(productMap || {}).length,
      filterValueCounts: {},
    };

    filterKeys.forEach((key) => {
      const unique = new Set();
      Object.values(productMap || {}).forEach((filters) => {
        (filters[key] || []).forEach((value) => {
          const normalized = normalizeValue(value);
          if (normalized) unique.add(normalized);
        });
      });
      summary.filterValueCounts[key] = unique.size;
    });

    return summary;
  }

  function parseJSON(el) {
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return {};
    }
  }

  function buildProductMapCacheKey(collectionHandle, apiVersion, filterKeys) {
    const handle = String(collectionHandle || '').trim().toLowerCase();
    const version = String(apiVersion || DEFAULT_API_VERSION).trim();
    if (!handle) return '';
    // Fold the active filter keys into the cache key so a cached productMap from a
    // session where fewer/different specs were configured can't be mistaken for
    // "complete" once the filter setup changes.
    const keysSignature = Array.from(new Set(filterKeys || [])).sort().join(',');
    return `nu_spec_cache_${handle}_${version}_${keysSignature}`;
  }

  function readProductMapCache(cacheKey, debugEnabled = false) {
    if (!cacheKey) return null;
    try {
      const raw = window.sessionStorage.getItem(cacheKey);
      if (!raw) {
        nuSpecDebug(debugEnabled, 'cache miss: productMap');
        return null;
      }
      const parsed = JSON.parse(raw);
      const ts = Number(parsed?.ts || 0);
      const map = parsed?.productMap;
      if (!ts || !map || typeof map !== 'object') {
        nuSpecDebug(debugEnabled, 'cache invalid: productMap');
        return null;
      }
      if (Date.now() - ts > PRODUCT_MAP_CACHE_TTL_MS) {
        nuSpecDebug(debugEnabled, 'cache expired: productMap');
        return null;
      }
      nuSpecDebug(debugEnabled, 'cache hit: productMap', { products: Object.keys(map).length });
      return map;
    } catch (error) {
      nuSpecDebug(debugEnabled, 'cache parse failed: productMap');
      return null;
    }
  }

  function writeProductMapCache(cacheKey, productMap) {
    if (!cacheKey || !productMap || typeof productMap !== 'object') return;
    try {
      window.sessionStorage.setItem(cacheKey, JSON.stringify({
        ts: Date.now(),
        productMap,
      }));
    } catch (error) {
      // Ignore storage quota and serialization failures.
    }
  }

  function buildRenderedProductsCacheKey(cacheKey) {
    if (!cacheKey) return '';
    return `${cacheKey}_rendered`;
  }

  function readRenderedProductsCache(cacheKey, debugEnabled = false) {
    if (!cacheKey) return null;
    try {
      const raw = window.sessionStorage.getItem(cacheKey);
      if (!raw) {
        nuSpecDebug(debugEnabled, 'cache miss: renderedProducts');
        return null;
      }
      const parsed = JSON.parse(raw);
      const ts = Number(parsed?.ts || 0);
      const renderedProducts = parsed?.renderedProducts;
      const isValid = renderedProducts
        && Array.isArray(renderedProducts.orderedHandles)
        && renderedProducts.handleToHtml
        && renderedProducts.handleToSkus;
      if (!ts || !isValid) {
        nuSpecDebug(debugEnabled, 'cache invalid: renderedProducts');
        return null;
      }
      if (Date.now() - ts > PRODUCT_MAP_CACHE_TTL_MS) {
        nuSpecDebug(debugEnabled, 'cache expired: renderedProducts');
        return null;
      }
      nuSpecDebug(debugEnabled, 'cache hit: renderedProducts', {
        products: renderedProducts.orderedHandles.length,
      });
      return renderedProducts;
    } catch (error) {
      nuSpecDebug(debugEnabled, 'cache parse failed: renderedProducts');
      return null;
    }
  }

  function writeRenderedProductsCache(cacheKey, renderedProducts) {
    if (!cacheKey || !renderedProducts || typeof renderedProducts !== 'object') return;
    try {
      window.sessionStorage.setItem(cacheKey, JSON.stringify({
        ts: Date.now(),
        renderedProducts,
      }));
    } catch (error) {
      // Ignore storage quota and serialization failures.
    }
  }

  function parseApiVersion(version) {
    const raw = String(version || '').trim();
    const match = raw.match(/^(\d{4})-(\d{2})$/);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
    if (month < 1 || month > 12) return null;
    return { year, month, raw };
  }

  function isApiVersionAtLeast(version, minimum) {
    const parsedVersion = parseApiVersion(version);
    const parsedMinimum = parseApiVersion(minimum);
    if (!parsedVersion || !parsedMinimum) return false;
    if (parsedVersion.year !== parsedMinimum.year) {
      return parsedVersion.year > parsedMinimum.year;
    }
    return parsedVersion.month >= parsedMinimum.month;
  }

  function getGuardedApiVersion(candidateVersion, debugEnabled = false) {
    if (isApiVersionAtLeast(candidateVersion, MIN_SUPPORTED_API_VERSION)) {
      return String(candidateVersion).trim();
    }
    nuSpecDebug(debugEnabled, 'storefront api version fallback', {
      requested: candidateVersion,
      resolved: DEFAULT_API_VERSION,
      minSupported: MIN_SUPPORTED_API_VERSION,
    });
    return DEFAULT_API_VERSION;
  }

  function normalizeValue(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return '';

    const collapseWhitespace = raw.replace(/\s+/g, ' ');

    if (/^-?\d+(?:\.\d+)?$/.test(collapseWhitespace)) {
      const num = Number(collapseWhitespace);
      return Number.isFinite(num) ? String(num) : collapseWhitespace;
    }

    const measurementMatch = collapseWhitespace.match(/^(-?\d+(?:\.\d+)?)\s+([a-z%°][a-z0-9%°]*)$/i);
    if (measurementMatch) {
      const num = Number(measurementMatch[1]);
      const unit = measurementMatch[2];
      if (Number.isFinite(num)) {
        return `${String(num)} ${unit}`;
      }
    }

    return collapseWhitespace;
  }

  function sanitizeIdPart(value, fallback = '') {
    const sanitized = String(value || '')
      .trim()
      .replace(/[^a-z0-9_-]+/gi, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (sanitized) return sanitized;
    return fallback;
  }

  function buildGroupOptionId(groupParam, optionValue, index, usedIds) {
    const paramPart = sanitizeIdPart(groupParam, 'nu-spec');
    const optionPart = sanitizeIdPart(normalizeValue(optionValue), `option-${index + 1}`);
    let candidate = `${paramPart}-${optionPart}`;
    let serial = 2;
    while (usedIds.has(candidate)) {
      candidate = `${paramPart}-${optionPart}-${serial}`;
      serial += 1;
    }
    usedIds.add(candidate);
    return candidate;
  }

  function normalizeNumericString(value) {
    const text = String(value || '').trim();
    if (!text) return '';
    if (!/^-?\d+(?:\.\d+)?$/.test(text)) return text;
    const num = Number(text);
    return Number.isFinite(num) ? String(num) : text;
  }

  function getComparableValueTokens(value) {
    const normalized = normalizeValue(value);
    if (!normalized) return [];

    const tokens = new Set([normalized]);
    const numericOnlyMatch = normalized.match(/^-?\d+(?:\.\d+)?$/);
    if (numericOnlyMatch) {
      const numeric = normalizeNumericString(normalized);
      if (numeric) tokens.add(numeric);
      return Array.from(tokens);
    }

    const measurementMatch = normalized.match(/^(-?\d+(?:\.\d+)?)\s+([a-z%°][a-z0-9%°]*)$/i);
    if (measurementMatch) {
      const numeric = normalizeNumericString(measurementMatch[1]);
      if (numeric) tokens.add(numeric);
    }

    return Array.from(tokens);
  }

  function hasComparableMatch(rawValues, selectedSet) {
    if (!selectedSet || selectedSet.size === 0) return true;

    const selectedTokens = new Set();
    selectedSet.forEach((selected) => {
      getComparableValueTokens(selected).forEach((token) => {
        selectedTokens.add(token);
      });
    });

    const productValues = toNormalizedDisplayValues(rawValues);
    for (let index = 0; index < productValues.length; index += 1) {
      const productValue = productValues[index];
      const tokens = getComparableValueTokens(productValue);
      for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex += 1) {
        if (selectedTokens.has(tokens[tokenIndex])) {
          return true;
        }
      }
    }

    return false;
  }

  function formatUnitLabel(unit) {
    const rawUnit = String(unit || '').trim();
    if (!rawUnit) return '';
    // Kept in sync with the Shopify-official unit list in snippets/nu--unit-label.liquid —
    // update both together (see M-16 in .doc/full-project-review/).
    const unitMap = {
      MILLIMETERS: 'mm',
      CENTIMETERS: 'cm',
      METERS: 'm',
      INCHES: 'in',
      FEET: 'ft',
      YARDS: 'yd',
      GRAMS: 'g',
      KILOGRAMS: 'kg',
      OUNCES: 'oz',
      POUNDS: 'lb',
      MILLILITERS: 'ml',
      CENTILITERS: 'cl',
      LITERS: 'L',
      CUBIC_METERS: 'm3',
      US_FLUID_OUNCES: 'fl oz',
      US_PINTS: 'pt',
      US_QUARTS: 'qt',
      US_GALLONS: 'gal',
      IMPERIAL_FLUID_OUNCES: 'imp fl oz',
      IMPERIAL_PINTS: 'imp pt',
      IMPERIAL_QUARTS: 'imp qt',
      IMPERIAL_GALLONS: 'imp gal',
    };
    if (unitMap[rawUnit]) return unitMap[rawUnit];
    return rawUnit.replace(/_/g, ' ').toLowerCase();
  }

  function toDisplayString(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return normalizeNumericString(value);
    }

    if (Array.isArray(value)) return '';
    if (typeof value !== 'object') return '';

    const baseValue = value.value ?? value.amount ?? value.label ?? value.name ?? value.title;
    if (baseValue !== null && baseValue !== undefined && baseValue !== '') {
      const text = normalizeNumericString(baseValue);
      if (!text) return '';
      const unit = formatUnitLabel(value.unit ?? value.unitCode ?? value.measurement_unit);
      return unit ? `${text} ${unit}` : text;
    }

    return '';
  }

  function toDisplayValues(value) {
    if (value === null || value === undefined || value === '') return [];

    if (Array.isArray(value)) {
      return value.flatMap((entry) => toDisplayValues(entry));
    }

    const asText = toDisplayString(value);
    if (asText) return [asText];

    if (typeof value === 'object') {
      if (Array.isArray(value.values)) {
        return value.values.flatMap((entry) => toDisplayValues(entry));
      }
      return [];
    }

    return [];
  }

  function toNormalizedDisplayValues(value) {
    return toDisplayValues(value)
      .map((entry) => normalizeValue(entry))
      .filter(Boolean);
  }

  function getGroupControlType(groupEl) {
    return groupEl?.dataset?.nuSpecControl || 'select';
  }

  function parseMetafieldValues(value) {
    if (value === null || value === undefined || value === '') return [];
    if (typeof value === 'object') return toDisplayValues(value);

    const raw = String(value).trim();
    if (!raw) return [];

    if ((raw.startsWith('[') && raw.endsWith(']')) || (raw.startsWith('{') && raw.endsWith('}'))) {
      try {
        const parsed = JSON.parse(raw);
        return toDisplayValues(parsed);
      } catch (error) {
        return [raw];
      }
    }

    return [raw];
  }

  function escapeGraphQLString(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function buildMetafieldDescriptors(keys) {
    const descriptors = [];
    keys.forEach((key) => {
      const [namespace, metafieldKey] = key.split('.');
      if (!namespace || !metafieldKey) return;
      descriptors.push({
        key,
        alias: `mf${descriptors.length}`,
        namespace,
        metafieldKey,
      });
    });
    return descriptors;
  }

  function buildMetafieldSelection(descriptors) {
    return descriptors.map((descriptor) => (
      `${descriptor.alias}: metafield(namespace: "${escapeGraphQLString(descriptor.namespace)}", key: "${escapeGraphQLString(descriptor.metafieldKey)}") { value }`
    )).join('\n');
  }

  // Tags an error with `nuErrorKind` ('timeout' | 'unavailable') so callers can classify
  // it without string-matching messages. Used to drive the cold-load error UI (scenario B/C).
  function taggedError(message, kind) {
    const error = new Error(message);
    error.nuErrorKind = kind;
    return error;
  }

  // Kinds with their own dedicated copy/code (E-1 timeout, E-4 channel_unpublished) —
  // anything else tagged by taggedError() (or untagged) falls back to the generic
  // 'unavailable' (E-2) message. Shared by both cold-load fetch call sites so a future
  // kind only needs to be added here once.
  const KNOWN_COLD_LOAD_ERROR_KINDS = ['timeout', 'channel_unpublished'];
  function classifyColdLoadErrorKind(error) {
    return KNOWN_COLD_LOAD_ERROR_KINDS.includes(error?.nuErrorKind) ? error.nuErrorKind : 'unavailable';
  }

  async function fetchWithTimeout(url, options, timeoutMs) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (error && error.name === 'AbortError') {
        throw taggedError('Storefront API request timed out', 'timeout');
      }
      throw error;
    } finally {
      window.clearTimeout(timer);
    }
  }

  async function fetchCollectionProductMap({
    collectionHandle,
    token,
    apiVersion,
    filterKeys,
    debugEnabled = false,
    availabilityInStockLabel = 'In Stock',
    availabilityOutStockLabel = 'Sold Out',
    timeoutMs = 8000,
  }) {
    if (!collectionHandle || !token || filterKeys.length === 0) return null;

    const endpoint = `/api/${apiVersion || DEFAULT_API_VERSION}/graphql.json`;
    const metafieldDescriptors = buildMetafieldDescriptors(filterKeys);
    const metafieldSelection = buildMetafieldSelection(metafieldDescriptors);
    const metafieldDescriptorByKey = Object.fromEntries(
      metafieldDescriptors.map((descriptor) => [descriptor.key, descriptor])
    );
    const query = `
      query NuSpecCollectionProducts($handle: String!, $after: String) {
        collection(handle: $handle) {
          products(first: ${PRODUCTS_PAGE_SIZE}, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                handle
                availableForSale
                ${metafieldSelection}
              }
            }
          }
        }
      }
    `;

    let after = null;
    let page = 0;
    let truncatedByPageLimit = false;
    const output = {};

    while (page < MAX_PAGE_FETCH) {
      page += 1;
      nuSpecDebug(debugEnabled, 'storefront fetch page:start', { page, after });
      const response = await fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query,
          variables: {
            handle: collectionHandle,
            after,
          },
        }),
      }, timeoutMs);

      if (!response.ok) {
        throw taggedError(`Storefront API request failed with ${response.status}`, 'unavailable');
      }

      const payload = await response.json();
      if (payload.errors && payload.errors.length > 0) {
        throw taggedError(payload.errors[0].message || 'Storefront API query error', 'unavailable');
      }

      // HTTP 200 with no GraphQL errors but `data` itself missing/null is a malformed
      // response (e.g. a CDN/proxy edge case) rather than a diagnosable channel issue —
      // without this check it would fall through to the `!connection` break below and
      // silently resolve as if the collection just had no more products.
      if (!payload?.data) {
        throw taggedError('Storefront API response missing data', 'unavailable');
      }

      // HTTP 200 with no GraphQL errors but `data.collection: null` means the collection
      // isn't visible to this Storefront API token's sales channel (e.g. not published to
      // the headless channel) — tagged as its own kind (not the generic 'unavailable') so
      // the UI can show a distinct, more specific message + error code for this diagnosable case.
      if (page === 1 && payload.data.collection === null) {
        throw taggedError('Collection not found via Storefront API (channel not published?)', 'channel_unpublished');
      }

      const connection = payload.data.collection?.products;
      if (!connection) break;

      connection.edges.forEach((edge) => {
        const node = edge.node;
        const handle = node?.handle;
        if (!handle) return;

        output[handle] = output[handle] || {};
        filterKeys.forEach((filterKey) => {
          if (filterKey === '__availability') {
            output[handle][filterKey] = [node?.availableForSale ? availabilityInStockLabel : availabilityOutStockLabel];
            return;
          }

          const descriptor = metafieldDescriptorByKey[filterKey];
          if (!descriptor) {
            output[handle][filterKey] = [];
            return;
          }

          const rawValue = node[descriptor.alias]?.value;
          output[handle][filterKey] = parseMetafieldValues(rawValue);
        });
      });

      if (!connection.pageInfo?.hasNextPage) break;
      if (page >= MAX_PAGE_FETCH) {
        truncatedByPageLimit = true;
        break;
      }
      after = connection.pageInfo.endCursor;
      if (!after) break;
    }

    nuSpecDebug(debugEnabled, 'storefront fetch done', {
      pagesFetched: page,
      products: Object.keys(output).length,
    });
    if (truncatedByPageLimit) {
      // productMap only ever holds PRODUCTS_PAGE_SIZE * MAX_PAGE_FETCH products — beyond
      // that, filter options and filtered results are silently incomplete with no UI cue,
      // so at minimum this needs to be loud in the console for whoever's debugging a
      // "missing products" report on a large collection.
      console.warn( // eslint-disable-line no-console
        `[nu-spec-filters] productMap truncated at ${PRODUCTS_PAGE_SIZE * MAX_PAGE_FETCH} products `
          + `(collection "${collectionHandle}" has more) — filter options and results will be incomplete.`
      );
    }
    return output;
  }

  function mergeProductMaps(baseMap, incomingMap) {
    const merged = { ...(baseMap || {}) };
    Object.entries(incomingMap || {}).forEach(([handle, filters]) => {
      merged[handle] = merged[handle] || {};
      Object.entries(filters || {}).forEach(([filterKey, values]) => {
        const existing = Array.isArray(merged[handle][filterKey]) ? merged[handle][filterKey] : [];
        const nextValues = Array.isArray(values) ? values : [];

        if (filterKey === '__availability') {
          const normalizedNext = toDisplayValues(nextValues);
          const normalizedExisting = toDisplayValues(existing);
          // Keep server-rendered availability stable once present.
          // This avoids late async sources flipping filter counts (e.g. 2 -> 1).
          merged[handle][filterKey] = normalizedExisting.length > 0 ? normalizedExisting : normalizedNext;
          return;
        }

        const unique = new Map();
        existing.forEach((value) => {
          toDisplayValues(value).forEach((displayValue) => {
            const trimmed = String(displayValue || '').trim();
            if (!trimmed) return;
            unique.set(normalizeValue(trimmed), trimmed);
          });
        });
        nextValues.forEach((value) => {
          toDisplayValues(value).forEach((displayValue) => {
            const trimmed = String(displayValue || '').trim();
            if (!trimmed) return;
            unique.set(normalizeValue(trimmed), trimmed);
          });
        });
        merged[handle][filterKey] = Array.from(unique.values());
      });
    });
    return merged;
  }

  function getSelectedValues(groupEl) {
    const selected = new Set();
    if (getGroupControlType(groupEl) === 'checkbox') {
      groupEl.querySelectorAll('[data-nu-spec-checkbox]:checked').forEach((checkbox) => {
        selected.add(normalizeValue(checkbox.value));
      });
      return selected;
    }

    const select = groupEl.querySelector('[data-nu-spec-select]');
    if (!select) return selected;
    Array.from(select.selectedOptions).forEach((option) => {
      selected.add(normalizeValue(option.value));
    });
    return selected;
  }

  function updateGroupClearButton(groupEl) {
    const clearBtn = groupEl.querySelector('[data-nu-spec-clear]');
    if (!clearBtn) return;
    const selectionCount = getSelectedValues(groupEl).size;
    const hasSelection = selectionCount > 0;
    clearBtn.hidden = !hasSelection;
    // Do not touch textContent here (M-49) — the button's translated label
    // (snippets/nu--spec-filters.liquid) is already correct as rendered; nothing else in this
    // file changes it dynamically (no count/state gets appended to it), so overwriting it with
    // a hardcoded English "Clear" on every selection change only ever destroyed the correct
    // localized text (e.g. "清除" on the tw store) without any offsetting benefit.
  }

  function parseSelectionsFromUrl(groups) {
    const params = new URLSearchParams(window.location.search);
    const selectionsByParam = new Map();

    groups.forEach((group) => {
      const param = group.dataset.param;
      const raw = params.get(param);
      if (!raw) return;
      const values = raw.split('|').map(normalizeValue).filter(Boolean);
      selectionsByParam.set(param, new Set(values));
    });

    return selectionsByParam;
  }

  function buildOptionsByFilterKey(productMap, filterKeys) {
    const optionsByFilterKey = {};
    filterKeys.forEach((key) => {
      optionsByFilterKey[key] = new Map();
    });

    Object.values(productMap).forEach((productFilters) => {
      filterKeys.forEach((key) => {
        const values = productFilters[key] || [];
        values.forEach((value) => {
          toDisplayValues(value).forEach((displayValue) => {
            const trimmed = String(displayValue || '').trim();
            if (!trimmed) return;
            const normalized = normalizeValue(trimmed);
            if (!optionsByFilterKey[key].has(normalized)) {
              optionsByFilterKey[key].set(normalized, trimmed);
            }
          });
        });
      });
    });

    const output = {};
    filterKeys.forEach((key) => {
      output[key] = Array.from(optionsByFilterKey[key].values())
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    });

    return output;
  }

  function extractGridItemsFromHtml(htmlText) {
    if (!htmlText || typeof htmlText !== 'string') return {
      orderedHandles: [],
      handleToHtml: {},
      handleToSkus: {},
    };

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, 'text/html');
    const output = {
      orderedHandles: [],
      handleToHtml: {},
      handleToSkus: {},
    };

    let items = doc.querySelectorAll('.main-products-grid__results [data-product-handle]');
    if (!items || items.length === 0) {
      items = doc.querySelectorAll('[data-product-handle]');
    }

    items.forEach((item) => {
      const handle = item.dataset.productHandle;
      if (!handle) return;
      if (!output.handleToHtml[handle]) {
        output.orderedHandles.push(handle);
        output.handleToHtml[handle] = item.outerHTML;
        output.handleToSkus[handle] = String(item.dataset.productSkus || '')
          .toLowerCase()
          .split('|')
          .map((sku) => sku.trim())
          .filter(Boolean);
      }
    });

    return output;
  }

  function extractGridItemsFromDom(rootEl) {
    if (!rootEl) return { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
    const output = {
      orderedHandles: [],
      handleToHtml: {},
      handleToSkus: {},
    };

    rootEl.querySelectorAll('[data-product-handle]').forEach((item) => {
      const handle = item.dataset.productHandle;
      if (!handle) return;
      if (!output.handleToHtml[handle]) {
        output.orderedHandles.push(handle);
        output.handleToHtml[handle] = item.outerHTML;
        output.handleToSkus[handle] = String(item.dataset.productSkus || '')
          .toLowerCase()
          .split('|')
          .map((sku) => sku.trim())
          .filter(Boolean);
      }
    });

    return output;
  }

  function mergeRenderedProducts(baseData, incomingData) {
    const merged = {
      orderedHandles: [...(baseData?.orderedHandles || [])],
      handleToHtml: { ...(baseData?.handleToHtml || {}) },
      handleToSkus: { ...(baseData?.handleToSkus || {}) },
    };

    (incomingData?.orderedHandles || []).forEach((handle) => {
      if (!merged.handleToHtml[handle] && incomingData.handleToHtml?.[handle]) {
        merged.orderedHandles.push(handle);
        merged.handleToHtml[handle] = incomingData.handleToHtml[handle];
      }
      if (!merged.handleToSkus[handle] && incomingData.handleToSkus?.[handle]) {
        merged.handleToSkus[handle] = incomingData.handleToSkus[handle];
      }
    });

    return merged;
  }

  async function fetchAllPagesRenderedProducts({
    sectionId, totalPages, currentPage, debugEnabled = false, signal = null
  }) {
    if (!sectionId || totalPages <= 0) return { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
    const currentUrl = new URL(window.location.href);

    function parseRenderedFromSectionResponse(payloadText) {
      if (!payloadText || typeof payloadText !== 'string') return { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };

      const fromHtml = extractGridItemsFromHtml(payloadText);
      if (fromHtml.orderedHandles.length > 0) return fromHtml;

      try {
        const jsonPayload = JSON.parse(payloadText);
        if (jsonPayload && typeof jsonPayload === 'object') {
          const sectionHtml = typeof jsonPayload[sectionId] === 'string'
            ? jsonPayload[sectionId]
            : Object.values(jsonPayload).find((value) => typeof value === 'string');
          if (sectionHtml) {
            return extractGridItemsFromHtml(sectionHtml);
          }
        }
      } catch (error) {
        return { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
      }

      return { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
    }

    async function fetchPageWithRetry(page) {
      const requestUrl = new URL(currentUrl.toString());
      requestUrl.searchParams.set('section_id', sectionId);
      requestUrl.searchParams.set('page', String(page));
      requestUrl.searchParams.set('_nu_spec_grid_fetch', String(page));

      let rendered = { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
      for (let attempt = 1; attempt <= 2; attempt += 1) {
        try {
          const response = await fetch(requestUrl.toString(), {
            credentials: 'same-origin',
            signal,
          });
          if (!response.ok) throw new Error(`section grid fetch failed: ${response.status}`);
          const responseText = await response.text();
          rendered = parseRenderedFromSectionResponse(responseText);
          if (rendered.orderedHandles.length > 0 || attempt === 2) break;
        } catch (error) {
          // A superseding call already aborted us — don't burn a second attempt retrying
          // a request nobody wants the result of anymore.
          if (error?.name === 'AbortError') break;
          if (attempt === 2) {
            rendered = { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
          }
        }
      }

      nuSpecDebug(debugEnabled, 'section rendered grid page parsed', {
        page,
        products: rendered.orderedHandles.length,
      });
      return rendered;
    }

    const pages = [];
    for (let page = 1; page <= totalPages; page += 1) pages.push(page);
    const renderedByPage = nuFiltersLite?.runWithConcurrency
      ? await nuFiltersLite.runWithConcurrency(pages, SECTION_FETCH_CONCURRENCY, fetchPageWithRetry)
      : await Promise.all(pages.map((page) => fetchPageWithRetry(page)));

    let merged = { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
    renderedByPage.forEach((rendered) => {
      merged = mergeRenderedProducts(merged, rendered);
    });

    return merged;
  }

  function buildCustomMultiSelectList(options, selectedValues = new Set(), groupLabel = '') {
    const listEl = document.createElement('ul');
    listEl.className = 'nu-spec-filters__multiselect-list';
    listEl.setAttribute('data-nu-spec-ms-list', '');
    listEl.setAttribute('role', 'listbox');
    listEl.setAttribute('aria-multiselectable', 'true');
    // role="listbox" replaces this <ul>'s native semantics, so it no longer inherits an
    // accessible name from the enclosing <fieldset>/<legend> the way the pre-hydration native
    // <select> fallback does (C-60) — without this a screen reader only announces "list box,
    // N items" with no indication of which filter group (e.g. "Product Family") it belongs to.
    if (groupLabel) listEl.setAttribute('aria-label', groupLabel);

    options.forEach((option, index) => {
      const rowEl = document.createElement('li');
      rowEl.className = 'nu-spec-filters__multiselect-option';
      rowEl.setAttribute('data-nu-spec-ms-row', '');
      rowEl.setAttribute('data-index', String(index));
      rowEl.setAttribute('data-value', option);
      rowEl.setAttribute('role', 'option');
      // Roving tabindex: only one row is ever in the Tab order at a time (this one, until
      // arrow-key navigation moves it — see bindCustomMultiSelectInteraction), so Tab enters
      // and leaves the whole listbox in a single stop instead of walking every option.
      rowEl.setAttribute('tabindex', index === 0 ? '0' : '-1');
      rowEl.setAttribute('aria-selected', selectedValues.has(normalizeValue(option)) ? 'true' : 'false');
      rowEl.classList.toggle('is-selected', selectedValues.has(normalizeValue(option)));

      const textEl = document.createElement('span');
      textEl.className = 'nu-spec-filters__multiselect-option-label';
      textEl.textContent = option;

      rowEl.appendChild(textEl);
      listEl.appendChild(rowEl);
    });

    return listEl;
  }

  function bindCustomMultiSelectInteraction(listEl, selectEl) {
    if (!listEl || !selectEl) return;
    if (listEl.dataset.nuSpecMsBound === 'true') return;
    listEl.dataset.nuSpecMsBound = 'true';
    let anchorIndex = -1;

    const syncRowsFromSelect = () => {
      const optionStateByValue = new Map();
      Array.from(selectEl.options).forEach((optionEl) => {
        optionStateByValue.set(normalizeValue(optionEl.value), {
          selected: optionEl.selected,
          disabled: optionEl.disabled,
        });
      });

      listEl.querySelectorAll('[data-nu-spec-ms-row]').forEach((rowEl) => {
        const state = optionStateByValue.get(normalizeValue(rowEl.getAttribute('data-value'))) || {
          selected: false,
          disabled: false,
        };
        const isSelected = state.selected;
        const isDisabled = state.disabled;
        rowEl.classList.toggle('is-selected', isSelected);
        rowEl.classList.toggle('is-disabled', isDisabled);
        rowEl.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        rowEl.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      });
    };

    const applySelectionToSelect = (nextSelectedSet) => {
      Array.from(selectEl.options).forEach((optionEl) => {
        optionEl.selected = nextSelectedSet.has(normalizeValue(optionEl.value));
      });
      syncRowsFromSelect();
      selectEl.dispatchEvent(new Event('change', { bubbles: true }));
    };

    listEl.addEventListener('click', (event) => {
      const rowEl = event.target.closest('[data-nu-spec-ms-row]');
      if (!rowEl) return;
      event.preventDefault();
      const index = Number(rowEl.getAttribute('data-index'));
      if (!Number.isFinite(index) || index < 0) return;
      if (rowEl.getAttribute('aria-disabled') === 'true') return;

      const optionEls = Array.from(selectEl.options);
      const clickedOption = optionEls[index];
      if (!clickedOption) return;
      const clickedValue = normalizeValue(clickedOption.value);
      const selectedSet = new Set(
        optionEls
          .filter((optionEl) => optionEl.selected)
          .map((optionEl) => normalizeValue(optionEl.value))
      );

      const useRange = event.shiftKey && anchorIndex >= 0;
      let nextSelectedSet = new Set(selectedSet);

      if (useRange) {
        const start = Math.min(anchorIndex, index);
        const end = Math.max(anchorIndex, index);
        for (let cursor = start; cursor <= end; cursor += 1) {
          const rangeOption = optionEls[cursor];
          if (!rangeOption) continue;
          nextSelectedSet.add(normalizeValue(rangeOption.value));
        }
      } else {
        if (nextSelectedSet.has(clickedValue)) {
          nextSelectedSet.delete(clickedValue);
        } else {
          nextSelectedSet.add(clickedValue);
        }
      }

      anchorIndex = index;
      applySelectionToSelect(nextSelectedSet);
    });

    listEl.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        const rowEl = event.target.closest('[data-nu-spec-ms-row]');
        if (!rowEl) return;
        if (rowEl.getAttribute('aria-disabled') === 'true') return;
        event.preventDefault();
        rowEl.click();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') return;
      // .hidden rows are ones the search box (see applyGroupSearchFilter) has filtered out
      // of view — they should be just as invisible to Arrow/Home/End as they are visually.
      const rows = Array.from(listEl.querySelectorAll('[data-nu-spec-ms-row]')).filter((rowEl) => !rowEl.hidden);
      if (rows.length === 0) return;
      const currentRow = event.target.closest('[data-nu-spec-ms-row]');
      const currentIndex = currentRow ? rows.indexOf(currentRow) : -1;

      let nextIndex;
      if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = rows.length - 1;
      } else if (event.key === 'ArrowDown') {
        nextIndex = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, rows.length - 1);
      } else {
        nextIndex = currentIndex < 0 ? rows.length - 1 : Math.max(currentIndex - 1, 0);
      }

      const nextRow = rows[nextIndex];
      if (!nextRow) return;
      event.preventDefault();
      listEl.querySelectorAll('[data-nu-spec-ms-row]').forEach((rowEl) => rowEl.setAttribute('tabindex', '-1'));
      nextRow.setAttribute('tabindex', '0');
      nextRow.focus();
    });

    syncRowsFromSelect();
  }

  function createSpecSearchWrap(groupLabel = '') {
    // Read straight off the container rather than threading it through every call site —
    // there's only ever one nu-spec-filters container per page (see initContainer()).
    const specFiltersDataset = document.querySelector('[data-nu-spec-filters]')?.dataset || {};
    const searchValuesLabel = specFiltersDataset.searchValuesLabel || 'Search Values';
    const searchGroupValuesTemplate = specFiltersDataset.searchGroupValuesTemplate || 'Search [label] values';
    const searchWrap = document.createElement('div');
    searchWrap.className = 'nu-spec-filters__search';

    const searchIcon = document.createElement('span');
    searchIcon.className = 'nu-spec-filters__search-icon';
    searchIcon.setAttribute('aria-hidden', 'true');
    searchIcon.innerHTML = '<svg class="icon icon--search" viewBox="0 0 24 24" aria-hidden="true"><path d="M15.5 15.5L20 20" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="10.5" cy="10.5" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.className = 'nu-spec-filters__search-input';
    searchInput.setAttribute('data-nu-spec-search', '');
    searchInput.placeholder = searchValuesLabel;
    searchInput.setAttribute(
      'aria-label',
      groupLabel ? searchGroupValuesTemplate.replace('[label]', groupLabel) : searchValuesLabel
    );
    searchInput.autocomplete = 'off';

    searchWrap.appendChild(searchIcon);
    searchWrap.appendChild(searchInput);
    return searchWrap;
  }

  function ensureGroupSearchWrap(group, contentWrap = null) {
    const contentEl = contentWrap || group?.querySelector('.nu-spec-filters__options .nu--facet-content');
    if (!contentEl) return null;
    let searchWrap = contentEl.querySelector('.nu-spec-filters__search');
    if (searchWrap) return searchWrap;
    const groupLabel = group?.querySelector('.nu-spec-filters__legend')?.textContent?.trim() || '';
    searchWrap = createSpecSearchWrap(groupLabel);
    contentEl.insertBefore(searchWrap, contentEl.firstChild);
    return searchWrap;
  }

  function renderGroupOptions(group, options, selectedValues = new Set()) {
    const optionsWrap = group.querySelector('.nu-spec-filters__options');
    if (!optionsWrap) return;

    // A rebuild (e.g. cold-load success, or mid-filtering hydration) wipes and rebuilds this
    // group's search input from scratch below — capture whatever the user already typed/was
    // doing so it isn't silently discarded out from under them.
    const previousSearchInput = optionsWrap.querySelector('[data-nu-spec-search]');
    const previousSearchValue = previousSearchInput?.value || '';
    const hadFocus = Boolean(previousSearchInput) && previousSearchInput === document.activeElement;
    const previousSelectionStart = hadFocus ? previousSearchInput.selectionStart : null;
    const previousSelectionEnd = hadFocus ? previousSearchInput.selectionEnd : null;

    optionsWrap.innerHTML = '';
    const contentWrap = document.createElement('div');
    contentWrap.className = 'filter__content nu--facet-content';

    if (getGroupControlType(group) === 'checkbox') {
      ensureGroupSearchWrap(group, contentWrap);
      const list = document.createElement('ul');
      list.setAttribute('role', 'list');
      const usedIds = new Set();

      options.forEach((option, index) => {
        const listItem = document.createElement('li');
        listItem.setAttribute('data-nu-spec-option-row', '');

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'checkbox visually-hidden focus-label-before';
        checkbox.value = option;
        checkbox.setAttribute('data-nu-spec-checkbox', '');
        checkbox.checked = selectedValues.has(normalizeValue(option));

        const optionId = buildGroupOptionId(group.dataset.param || 'nu-spec', option, index, usedIds);
        checkbox.id = optionId;

        const label = document.createElement('label');
        label.className = 'filter__label flex items-start w-full m-0';
        label.setAttribute('for', optionId);

        const text = document.createElement('span');
        text.className = 'flex-auto';
        text.textContent = option;

        label.appendChild(text);
        listItem.appendChild(checkbox);
        listItem.appendChild(label);
        list.appendChild(listItem);
      });
      contentWrap.appendChild(list);
      optionsWrap.appendChild(contentWrap);
    } else {
      const searchWrap = ensureGroupSearchWrap(group, contentWrap);

      const select = document.createElement('select');
      select.className = 'nu-spec-filters__select';
      select.multiple = true;
      select.setAttribute('data-nu-spec-select', '');
      select.hidden = true;

      options.forEach((option) => {
        const optionEl = document.createElement('option');
        optionEl.value = option;
        optionEl.textContent = option;
        optionEl.selected = selectedValues.has(normalizeValue(option));
        select.appendChild(optionEl);
      });

      const groupLabel = group.querySelector('.nu-spec-filters__legend')?.textContent?.trim() || '';
      const listEl = buildCustomMultiSelectList(options, selectedValues, groupLabel);

      contentWrap.appendChild(searchWrap);
      contentWrap.appendChild(listEl);
      contentWrap.appendChild(select);
      optionsWrap.appendChild(contentWrap);
      bindCustomMultiSelectInteraction(listEl, select);
    }
    if (previousSearchValue) {
      const newSearchInput = optionsWrap.querySelector('[data-nu-spec-search]');
      if (newSearchInput) {
        newSearchInput.value = previousSearchValue;
        if (hadFocus) {
          newSearchInput.focus();
          if (previousSelectionStart !== null && previousSelectionEnd !== null) {
            newSearchInput.setSelectionRange(previousSelectionStart, previousSelectionEnd);
          }
        }
      }
    }

    // A class instead of the `hidden` attribute — [hidden] is styled `display: none
    // !important` by the theme's own vendor-maintained main.css, so keeping this group
    // visible-but-invisible during the loading skeleton (see .nu-spec-filters__groups
    // [data-nu-loading="true"] in nu--spec-filters.css) would otherwise require an
    // !important of our own just to win the cascade back.
    group.classList.toggle('nu-spec-filters__group--empty', options.length === 0);
  }

  function applyGroupSearchFilter(group) {
    const searchInput = group.querySelector('[data-nu-spec-search]');
    if (!searchInput) return;

    const keyword = normalizeValue(searchInput.value);
    if (getGroupControlType(group) === 'checkbox') {
      group.querySelectorAll('[data-nu-spec-option-row]').forEach((rowEl) => {
        const checkbox = rowEl.querySelector('[data-nu-spec-checkbox]');
        const textValue = checkbox ? checkbox.value : rowEl.textContent;
        const text = normalizeValue(textValue);
        rowEl.hidden = keyword.length > 0 && !text.includes(keyword);
      });
      return;
    }

    const select = group.querySelector('[data-nu-spec-select]');
    if (!select) return;
    const multiRows = group.querySelectorAll('[data-nu-spec-ms-row]');
    if (multiRows.length > 0) {
      const rows = Array.from(multiRows);
      rows.forEach((rowEl) => {
        const text = normalizeValue(rowEl.getAttribute('data-value') || rowEl.textContent);
        rowEl.hidden = keyword.length > 0 && !text.includes(keyword);
      });
      // Filtering can hide the row currently holding the roving tabindex (see
      // buildCustomMultiSelectList), which would leave the listbox with no Tab stop at
      // all — re-anchor it to the first still-visible row when that happens.
      const visibleRows = rows.filter((rowEl) => !rowEl.hidden);
      if (visibleRows.length > 0 && !visibleRows.some((rowEl) => rowEl.getAttribute('tabindex') === '0')) {
        rows.forEach((rowEl) => rowEl.setAttribute('tabindex', '-1'));
        visibleRows[0].setAttribute('tabindex', '0');
      }
      return;
    }
    Array.from(select.options).forEach((option) => {
      const text = normalizeValue(option.textContent || option.value);
      option.hidden = keyword.length > 0 && !text.includes(keyword);
    });
  }

  function updateGroupSearchVisibility(group, threshold) {
    const controlType = getGroupControlType(group);
    const searchWrap = controlType === 'checkbox'
      ? ensureGroupSearchWrap(group)
      : group.querySelector('.nu-spec-filters__search');
    const searchInput = group.querySelector('[data-nu-spec-search]');
    if (!searchWrap) return;

    let optionsCount = 0;
    if (controlType === 'checkbox') {
      optionsCount = group.querySelectorAll('[data-nu-spec-checkbox]').length;
    } else {
      const multiRows = group.querySelectorAll('[data-nu-spec-ms-row]');
      if (multiRows.length > 0) {
        optionsCount = multiRows.length;
      } else {
        const select = group.querySelector('[data-nu-spec-select]');
        if (!select) return;
        optionsCount = select.options.length;
      }
    }

    const shouldShowSearch = optionsCount >= threshold;
    searchWrap.hidden = !shouldShowSearch;

    if (!shouldShowSearch) {
      if (searchInput) searchInput.value = '';
      if (controlType === 'checkbox') {
        group.querySelectorAll('[data-nu-spec-option-row]').forEach((rowEl) => {
          rowEl.hidden = false;
        });
      } else {
        const multiRows = group.querySelectorAll('[data-nu-spec-ms-row]');
        if (multiRows.length > 0) {
          multiRows.forEach((rowEl) => {
            rowEl.hidden = false;
          });
        } else {
          const select = group.querySelector('[data-nu-spec-select]');
          if (!select) return;
          Array.from(select.options).forEach((option) => {
            option.hidden = false;
          });
        }
      }
    }
  }

  function getGroupOptionRows(group) {
    if (!group) return [];
    const multiRows = Array.from(group.querySelectorAll('[data-nu-spec-ms-row]'));
    if (multiRows.length > 0) return multiRows;

    const checkboxRows = Array.from(group.querySelectorAll('[data-nu-spec-option-row]'));
    if (checkboxRows.length > 0) return checkboxRows;

    const list = group.querySelector('.nu--facet-content > ul');
    if (!list) return [];
    return Array.from(list.children).filter((el) => el.tagName === 'LI');
  }

  function ensureMobileShowMoreButton(group) {
    const clearWrap = group.querySelector('.nu-spec-filters__clear-wrap');
    if (!clearWrap) return null;

    let button = clearWrap.querySelector('.js-nu-spec-show-more');
    if (button) return button;

    button = document.createElement('button');
    button.type = 'button';
    button.className = 'filter__more link text-current text-sm js-nu-spec-show-more';
    // M-50: this button is built entirely client-side (no SSR counterpart to inherit text
    // from, unlike the per-group Clear button — see updateGroupClearButton()), so the
    // translated label has to be threaded through from Liquid via the group's own dataset
    // (snippets/nu--spec-filters.liquid sets data-show-more-label from the same
    // products.filtering.show_more key the native filter drawer already uses).
    button.textContent = group.dataset.showMoreLabel || 'Show more';
    button.hidden = true;
    clearWrap.insertBefore(button, clearWrap.firstChild);
    return button;
  }

  function updateGroupMobileOptionLimit(group, maxFilterOptions, mobileActive) {
    const optionRows = getGroupOptionRows(group);
    optionRows.forEach((rowEl) => {
      rowEl.classList.remove('nu-spec-filters__option-row--mobile-hidden');
    });

    const showMoreBtn = ensureMobileShowMoreButton(group);
    if (!showMoreBtn) return;

    if (maxFilterOptions < 2 || optionRows.length <= maxFilterOptions) {
      showMoreBtn.hidden = true;
      return;
    }

    // applyGroupSearchFilter() only ever toggles `hidden` on rows — it doesn't know about
    // this class, so a match past maxFilterOptions would otherwise stay display:none with no
    // way for the customer to reach it. While a keyword is active, leave every match reachable
    // instead of silently collapsing some of them behind "Show more".
    const searchInput = group.querySelector('[data-nu-spec-search]');
    const hasActiveSearch = Boolean(searchInput && normalizeValue(searchInput.value).length > 0);
    if (hasActiveSearch) {
      showMoreBtn.hidden = true;
      return;
    }

    const isExpanded = group.dataset.nuMobileOptionsExpanded === 'true';
    if (mobileActive && !isExpanded) {
      optionRows.forEach((rowEl, index) => {
        if (index >= maxFilterOptions) {
          rowEl.classList.add('nu-spec-filters__option-row--mobile-hidden');
        }
      });
    }

    showMoreBtn.hidden = !mobileActive || isExpanded;
  }

  function initContainer(container) {
    if (container.dataset.nuSpecInitialized === 'true') return;
    container.dataset.nuSpecInitialized = 'true';

    const fallbackProductMap = parseJSON(container.querySelector('[data-nu-spec-product-map]'));
    const groups = Array.from(container.querySelectorAll('[data-nu-spec-group]'));
    const productsTable = document.querySelector('.nu-products-table');
    const tableFooter = document.querySelector('.nu-products-table__footer');
    const specFiltersBody = container.querySelector('[data-nu-spec-body]');
    // The error/empty-state markup below all lives inside <template> tags so it never
    // lands in the rendered (or crawlable) DOM for the vast majority of visits where
    // nothing goes wrong. mountErrorTemplate() clones it into place the first time it's
    // actually needed; each is mounted at most once per page load.
    // Lives in .main-products-grid__results (not inside container/specFiltersBody) so it
    // stays visible on mobile even though the filter drawer itself is off-canvas there.
    const emptyNoticeTemplate = document.querySelector('.main-products-grid__results template[data-nu-error-template="empty"]');
    const coldLoadErrorTemplate = container.querySelector('template[data-nu-error-template="cold-load"]');
    const renderErrorTemplate = document.querySelector('.main-products-grid__results template[data-nu-error-template="render"]');
    let emptyNotice = null;
    let coldLoadErrorWrap = null;
    let coldLoadErrorPanels = [];
    let renderErrorPanel = null;

    function mountErrorTemplate(templateEl) {
      if (!templateEl) return null;
      const mountedEl = templateEl.content.firstElementChild.cloneNode(true);
      templateEl.replaceWith(mountedEl);
      return mountedEl;
    }

    function ensureEmptyNoticeMounted() {
      if (!emptyNotice) emptyNotice = mountErrorTemplate(emptyNoticeTemplate);
      return emptyNotice;
    }

    function ensureRenderErrorPanelMounted() {
      if (!renderErrorPanel) renderErrorPanel = mountErrorTemplate(renderErrorTemplate);
      return renderErrorPanel;
    }

    function ensureColdLoadErrorMounted() {
      if (coldLoadErrorWrap) return coldLoadErrorWrap;
      coldLoadErrorWrap = mountErrorTemplate(coldLoadErrorTemplate);
      if (coldLoadErrorWrap) {
        coldLoadErrorPanels = Array.from(coldLoadErrorWrap.querySelectorAll('[data-nu-error-panel]'));
        coldLoadErrorWrap.addEventListener('click', (event) => {
          const retryBtn = event.target.closest('[data-nu-error-action="retry"]');
          if (!retryBtn || retryBtn.disabled) return;
          retryBtn.disabled = true;
          coldLoadPromise = runColdLoadFetch();
          coldLoadPromise.finally(() => {
            retryBtn.disabled = false;
          });
        });
      }
      return coldLoadErrorWrap;
    }
    // <products-toolbar class="products-toolbar ..."> — 'products-toolbar' and '.products-toolbar'
    // both resolve to this same singleton element; only ever toggled/read, never replaced.
    const productsToolbarEl = document.querySelector('products-toolbar');
    const toolbarSkuSearchForm = document.querySelector('[data-nu-sku-search-form]');
    const toolbarRowsControl = document.querySelector('.nu-products-table__rows-control');
    const toolbarTotalEl = document.querySelector('[data-nu-products-total]');
    const toolbarSortControlsEl = document.querySelector('.nu-products-toolbar__controls');
    const mobileFiltersWrap = container.closest('.main-products-grid__filters');
    const mobileFiltersOpenBtn = document.querySelector('[data-nu-mobile-filters-open]');
    // Innerhtml gets swapped (cold-load restore, client pagination re-render) but the
    // wrapping <nav> element itself is never replaced, so it's safe to cache once.
    // A collection that fits in one default-size page has no server-rendered <nav> at all
    // (snippets/nu--pagination.liquid only renders it for paginate.pages > 1), so fall back to
    // creating an empty one (H-40) — otherwise every client-side pagination render below is
    // skipped and a smaller rows-per-page leaves the remaining products unreachable.
    const paginationWrap = document.querySelector('.main-products-grid__results nav[aria-label]')
      || nuFiltersLite?.ensurePaginationWrap?.(getPaginationLabels().wrap)
      || null;
    const errorTimeoutSecondsRaw = Number(container.dataset.errorTimeoutSeconds || '8');
    const errorTimeoutMs = (Number.isFinite(errorTimeoutSecondsRaw) && errorTimeoutSecondsRaw > 0
      ? errorTimeoutSecondsRaw
      : 8) * 1000;
    const clearAllBtn = container.querySelector('[data-nu-spec-clear-all]');
    const applyBtn = container.querySelector('[data-nu-spec-apply]');
    const appliedWrap = container.querySelector('[data-nu-spec-applied]');
    const appliedValuesWrap = container.querySelector('[data-nu-spec-applied-values]');
    const groupsContainers = Array.from(container.querySelectorAll('.nu-spec-filters__groups'));
    const mobileAppliedHost = document.querySelector('[data-nu-mobile-applied-filters]');
    const appliedWrapOriginalParent = appliedWrap ? appliedWrap.parentElement : null;
    const appliedWrapOriginalNextSibling = appliedWrap ? appliedWrap.nextElementSibling : null;
    const clearAllBtnOriginalParent = clearAllBtn ? clearAllBtn.parentElement : null;
    const clearAllBtnOriginalNextSibling = clearAllBtn ? clearAllBtn.nextElementSibling : null;
    const clearFilterLabel = container.dataset.clearFilterLabel || 'Clear Filter';
    const appliedPreviewCountRaw = Number(container.dataset.appliedPreviewCount || '2');
    const appliedPreviewCount = Number.isFinite(appliedPreviewCountRaw) && appliedPreviewCountRaw > 0
      ? appliedPreviewCountRaw
      : 2;
    const availabilityInStockLabel = container.dataset.availabilityInStockLabel || 'In Stock';
    const availabilityOutStockLabel = container.dataset.availabilityOutStockLabel || 'Sold Out';
    const removeFilterTemplate = container.dataset.removeFilterTemplate || 'Remove filter [label]';
    const overflowFilterOneTemplate = container.dataset.overflowFilterOneTemplate || 'other [count] filter';
    const overflowFilterOtherTemplate = container.dataset.overflowFilterOtherTemplate || 'other [count] filters';
    const storefrontToken = container.dataset.storefrontToken || '';
    const collectionHandle = container.dataset.collectionHandle || '';
    const debugEnabled = isNuSpecDebugEnabled();
    const apiVersion = getGuardedApiVersion(
      container.dataset.storefrontApiVersion || DEFAULT_API_VERSION,
      debugEnabled
    );
    const productMapCacheKey = buildProductMapCacheKey(
      collectionHandle,
      apiVersion,
      groups.map((group) => group.dataset.filterKey).filter(Boolean)
    );
    const cachedProductMap = readProductMapCache(productMapCacheKey, debugEnabled);
    const renderedProductsCacheKey = buildRenderedProductsCacheKey(productMapCacheKey);
    const cachedRenderedProducts = readRenderedProductsCache(renderedProductsCacheKey, debugEnabled);
    const sectionId = container.dataset.sectionId || '';
    const totalPages = Number(container.dataset.totalPages || '1');
    const currentPage = Number(container.dataset.currentPage || '1');
    const searchThreshold = Number(container.dataset.searchThreshold || '4');
    const maxFilterOptionsRaw = Number(container.dataset.maxFilterOptions || '0');
    const maxFilterOptions = Number.isFinite(maxFilterOptionsRaw) ? Math.floor(maxFilterOptionsRaw) : 0;
    const mobileFilterMql = window.matchMedia ? window.matchMedia(MOBILE_FILTER_QUERY) : null;
    const isMobileFilterViewport = () => (mobileFilterMql ? mobileFilterMql.matches : false);
    const resultsContainer = container.closest('[data-num-results]') || document.querySelector('[data-num-results]');
    const baseResultsCount = Number(resultsContainer?.dataset?.numResults || '0');
    const initialRenderedProducts = extractGridItemsFromDom(document);
    let productMap = fallbackProductMap;
    if (cachedProductMap) {
      productMap = mergeProductMaps(productMap, cachedProductMap);
    }
    const hasCachedProductMap = Boolean(cachedProductMap && Object.keys(cachedProductMap).length > 0);
    let urlSelections = new Map();
    let appliedSelectionsByParam = new Map();
    let hasPendingChanges = false;
    let appliedSelectionCount = 0;
    let appliedFiltersExpanded = false;
    let renderedProducts = cachedRenderedProducts && typeof cachedRenderedProducts === 'object'
      ? mergeRenderedProducts(initialRenderedProducts, cachedRenderedProducts)
      : initialRenderedProducts;
    let renderedProductsFetchToken = 0;
    // See the comment at the top of applySelections() for what this guards against.
    let applySelectionsToken = 0;
    // Fallback for nuFiltersLite?.nextR4QRefreshVersion() when NuFiltersLite hasn't loaded —
    // must stay in the same small-integer number space as that function (and as
    // nu--products-toolbar.js's own fallback), never a timestamp: whichever side dispatches the
    // bigger number becomes the new floor for "is this version newer", and a timestamp would
    // permanently outrank every future small-integer version on the other side.
    let r4qRefreshVersionFallbackCounter = 0;
    // Tracks the network requests belonging to the current refreshRenderedProductsForCurrentUrl()
    // call — renderedProductsFetchToken already discards a stale call's *result*, but without
    // this its in-flight fetches kept running (and consuming a slot in the concurrency pool)
    // even after nobody could use their output anymore.
    let renderedProductsFetchController = null;
    let unfilteredGridHtml = '';
    let originalPaginationHtml = '';
    let currentSkuQuery = '';
    let productsTableRenderLoadingCount = 0;
    let storefrontFetchPromise = null;
    let disabledRecomputeScheduled = false;
    let coldLoadPromise = null;
    let coldLoadFailed = false;

    function setGroupsLoading(isLoading) {
      groupsContainers.forEach((groupsEl) => {
        if (isLoading) {
          const currentHeight = groupsEl.offsetHeight || groupsEl.scrollHeight;
          if (currentHeight > 0) {
            groupsEl.style.minHeight = `${currentHeight}px`;
          }
          groupsEl.setAttribute('data-nu-loading', 'true');
        } else {
          groupsEl.removeAttribute('data-nu-loading');
          groupsEl.style.removeProperty('min-height');
        }
      });
    }

    function setProductsTableLoading(isLoading) {
      if (!productsTable) return;
      if (isLoading) {
        productsTable.setAttribute('data-nu-loading', 'true');
      } else {
        productsTable.removeAttribute('data-nu-loading');
      }
    }

    function beginProductsTableRenderLoading() {
      productsTableRenderLoadingCount += 1;
      if (productsTableRenderLoadingCount === 1) {
        setProductsTableLoading(true);
      }
    }

    function endProductsTableRenderLoading() {
      productsTableRenderLoadingCount = Math.max(0, productsTableRenderLoadingCount - 1);
      if (productsTableRenderLoadingCount === 0) {
        setProductsTableLoading(false);
      }
    }

    function waitForNextFrame() {
      return new Promise((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
    }

    function hasEnoughProductMap() {
      if (!Number.isFinite(baseResultsCount) || baseResultsCount <= 0) return true;
      if (Object.keys(productMap || {}).length < baseResultsCount) return false;
      // Key count alone isn't enough: the server-rendered fallback map only carries
      // __availability, so also confirm at least one product already has real spec/feature
      // filter values (from a prior storefront hydration) before treating the map as complete.
      if (!storefrontFilterKeys || storefrontFilterKeys.length === 0) return true;
      return Object.values(productMap).some((productFilters) => (
        storefrontFilterKeys.some((key) => Array.isArray(productFilters[key]) && productFilters[key].length > 0)
      ));
    }

    // Shared by ensureProductMapHydratedForFiltering() and runColdLoadFetch() so the eager
    // idle-triggered cold load and an on-demand filtering fetch never end up as two
    // independent in-flight requests for the same data — whichever calls first wins, and
    // everyone else awaits that same promise instead of racing it with their own.
    function fetchSharedStorefrontProductMap() {
      if (!storefrontFetchPromise) {
        storefrontFetchPromise = fetchCollectionProductMap({
          collectionHandle,
          token: storefrontToken,
          apiVersion,
          filterKeys,
          debugEnabled,
          availabilityInStockLabel,
          availabilityOutStockLabel,
          timeoutMs: errorTimeoutMs,
        }).finally(() => {
          storefrontFetchPromise = null;
        });
      }
      return storefrontFetchPromise;
    }

    async function ensureProductMapHydratedForFiltering() {
      if (coldLoadFailed) return;
      if (hasEnoughProductMap()) return;
      try {
        const storefrontMap = await fetchSharedStorefrontProductMap();
        if (!storefrontMap || Object.keys(storefrontMap).length === 0) return;
        productMap = mergeProductMaps(productMap, storefrontMap);
        writeProductMapCache(productMapCacheKey, productMap);
        rebuildOptionsFromProductMap(productMap);
        recomputeLinkedOptionDisabledStates();
      } catch (error) {
        nuSpecDebug(debugEnabled, 'storefront fetch for filtering failed', error?.message || error);
        // Same storefront call as the initial cold load (see runColdLoadFetch) — a
        // failure here means filtering has no real facet data to work with either,
        // so surface the same scenario B/C error UI instead of silently filtering
        // against the incomplete fallback map.
        showColdLoadError(classifyColdLoadErrorKind(error));
      }
    }

    function reorderSpecGroupsBySort() {
      const specGroups = Array.from(
        container.querySelectorAll('[data-nu-spec-group][data-nu-spec-control="select"]')
      );
      if (specGroups.length < 2) return;

      const parent = specGroups[0].parentElement;
      if (!parent) return;

      specGroups.sort((a, b) => {
        // A non-numeric metaobject "sort" value (a merchant typo, not just a blank field —
        // nu--spec-filters.liquid only defaults blank values) makes Number(...) produce NaN
        // (C-61); aOrder - bOrder would then also be NaN, an invalid comparator return value
        // that leaves the affected group(s)' relative order engine-dependent instead of just
        // falling back to "sort last" like a genuinely blank value already does.
        const aOrderRaw = Number(a.dataset.nuSpecOrder || Number.MAX_SAFE_INTEGER);
        const bOrderRaw = Number(b.dataset.nuSpecOrder || Number.MAX_SAFE_INTEGER);
        const aOrder = Number.isFinite(aOrderRaw) ? aOrderRaw : Number.MAX_SAFE_INTEGER;
        const bOrder = Number.isFinite(bOrderRaw) ? bOrderRaw : Number.MAX_SAFE_INTEGER;
        if (aOrder === bOrder) return 0;
        return aOrder - bOrder;
      });

      specGroups.forEach((group) => {
        parent.appendChild(group);
      });
    }

    nuSpecDebug(debugEnabled, 'init', {
      collectionHandle,
      sectionId,
      totalPages,
      currentPage,
      groups: groups.map((group) => group.dataset.filterKey),
      fallbackProducts: Object.keys(fallbackProductMap || {}).length,
    });

    function getSelections() {
      return groups.map((group) => ({
        group,
        filterKey: group.dataset.filterKey,
        param: group.dataset.param,
        selected: getSelectedValues(group),
      }));
    }

    function updateUrl(selections) {
      const url = new URL(window.location.href);
      selections.forEach((item) => {
        url.searchParams.delete(item.param);
      });
      selections.forEach((item) => {
        if (item.selected.size > 0) {
          url.searchParams.set(item.param, Array.from(item.selected).join('|'));
        }
      });
      if (currentSkuQuery) {
        url.searchParams.set('nu_sku', currentSkuQuery);
      } else {
        url.searchParams.delete('nu_sku');
      }
      history.replaceState({}, '', url.toString());
    }

    function getSkuQueryFromUrl() {
      const params = new URLSearchParams(window.location.search);
      return String(params.get('nu_sku') || '').trim().toLowerCase();
    }

    function cloneSelectionsMap(sourceMap) {
      const cloned = new Map();
      sourceMap.forEach((setValues, key) => {
        cloned.set(key, new Set(setValues));
      });
      return cloned;
    }

    function updateClearAllButton() {
      if (!clearAllBtn) return;
      clearAllBtn.hidden = appliedSelectionCount === 0;
      clearAllBtn.textContent = `${clearFilterLabel} (${appliedSelectionCount})`;
    }

    function setPendingChanges(pending) {
      hasPendingChanges = pending;
      if (applyBtn) {
        applyBtn.disabled = !hasPendingChanges;
      }
    }

    function updateAppliedFiltersSummary() {
      if (!appliedWrap || !appliedValuesWrap) return;
      const appliedEntries = [];
      appliedValuesWrap.innerHTML = '';

      groups.forEach((group) => {
        const groupLabel = group.querySelector('.nu-spec-filters__legend')?.textContent?.trim() || '';
        const param = group.dataset.param;
        const appliedValues = appliedSelectionsByParam.get(param);
        if (!appliedValues || appliedValues.size === 0) return;

        if (getGroupControlType(group) === 'checkbox') {
          group.querySelectorAll('[data-nu-spec-checkbox]').forEach((checkbox) => {
            const normalized = normalizeValue(checkbox.value);
            if (!appliedValues.has(normalized)) return;
            const optionLabel = (checkbox.value || '').trim();
            if (!optionLabel) return;
            const chipLabel = groupLabel ? `${groupLabel}: ${optionLabel}` : optionLabel;
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'nu-spec-filters__applied-chip text-sm';
            chip.setAttribute('data-nu-spec-applied-remove', 'true');
            chip.setAttribute('data-param', param);
            chip.setAttribute('data-value', normalized);
            chip.setAttribute('aria-label', removeFilterTemplate.replace('[label]', chipLabel));
            const chipLabelSpan = document.createElement('span');
            chipLabelSpan.textContent = chipLabel;
            const chipRemoveIconSpan = document.createElement('span');
            chipRemoveIconSpan.className = 'nu-spec-filters__applied-chip-x';
            chipRemoveIconSpan.setAttribute('aria-hidden', 'true');
            chipRemoveIconSpan.textContent = '×';
            chip.append(chipLabelSpan, chipRemoveIconSpan);
            appliedEntries.push({
              groupLabel,
              optionLabel,
              chip,
            });
          });
        } else {
          const select = group.querySelector('[data-nu-spec-select]');
          if (!select) return;
          Array.from(select.options).forEach((option) => {
            const normalized = normalizeValue(option.value);
            if (!appliedValues.has(normalized)) return;
            const optionLabel = (option.textContent || option.value || '').trim();
            if (!optionLabel) return;
            const chipLabel = groupLabel ? `${groupLabel}: ${optionLabel}` : optionLabel;
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'nu-spec-filters__applied-chip text-sm';
            chip.setAttribute('data-nu-spec-applied-remove', 'true');
            chip.setAttribute('data-param', param);
            chip.setAttribute('data-value', normalized);
            chip.setAttribute('aria-label', removeFilterTemplate.replace('[label]', chipLabel));
            const chipLabelSpan = document.createElement('span');
            chipLabelSpan.textContent = chipLabel;
            const chipRemoveIconSpan = document.createElement('span');
            chipRemoveIconSpan.className = 'nu-spec-filters__applied-chip-x';
            chipRemoveIconSpan.setAttribute('aria-hidden', 'true');
            chipRemoveIconSpan.textContent = '×';
            chip.append(chipLabelSpan, chipRemoveIconSpan);
            appliedEntries.push({
              groupLabel,
              optionLabel,
              chip,
            });
          });
        }
      });

      const totalEntries = appliedEntries.length;
      if (totalEntries === 0) {
        appliedWrap.hidden = true;
        syncAppliedFiltersPlacement();
        return;
      }

      const visibleCount = appliedFiltersExpanded ? totalEntries : Math.min(appliedPreviewCount, totalEntries);
      const visibleEntries = appliedEntries.slice(0, visibleCount);
      visibleEntries.forEach((entry) => {
        appliedValuesWrap.appendChild(entry.chip);
      });

      if (!appliedFiltersExpanded && totalEntries > visibleCount) {
        const overflowCount = totalEntries - visibleCount;
        const overflowEntries = appliedEntries.slice(visibleCount);
        const overflowWrap = document.createElement('span');
        overflowWrap.className = 'nu-spec-filters__overflow-wrap';

        const overflowBtn = document.createElement('button');
        overflowBtn.type = 'button';
        overflowBtn.className = 'link text-current text-sm';
        overflowBtn.setAttribute('data-nu-spec-applied-overflow', 'true');
        const overflowTemplate = overflowCount === 1 ? overflowFilterOneTemplate : overflowFilterOtherTemplate;
        overflowBtn.textContent = overflowTemplate.replace('[count]', String(overflowCount));

        overflowWrap.appendChild(overflowBtn);

        if (overflowEntries.length > 0) {
          const groupedOverflow = new Map();
          overflowEntries.forEach((entry) => {
            const label = entry.groupLabel || '';
            if (!groupedOverflow.has(label)) {
              groupedOverflow.set(label, []);
            }
            groupedOverflow.get(label).push(entry.optionLabel);
          });
          const overflowText = Array.from(groupedOverflow.values())
            .map((options) => options.join(', '))
            .join(' / ');

          const overflowPopover = document.createElement('div');
          overflowPopover.className = 'nu-spec-filters__overflow-popover';
          overflowPopover.setAttribute('aria-hidden', 'true');
          overflowPopover.textContent = overflowText;
          overflowWrap.appendChild(overflowPopover);
        }

        appliedValuesWrap.appendChild(overflowWrap);
      }

      appliedWrap.hidden = false;
      syncAppliedFiltersPlacement();
    }

    function syncAppliedFiltersPlacement() {
      if (!appliedWrap || !mobileAppliedHost) return;
      const isMobile = window.matchMedia('(max-width: 768.98px)').matches;
      if (isMobile) {
        if (appliedWrap.parentElement !== mobileAppliedHost) {
          mobileAppliedHost.appendChild(appliedWrap);
        }
        if (clearAllBtn) {
          const appliedLabelEl = appliedWrap.querySelector('.nu-spec-filters__applied-label');
          if (appliedLabelEl && clearAllBtn.parentElement !== appliedWrap) {
            appliedLabelEl.insertAdjacentElement('afterend', clearAllBtn);
          }
        }
        // Also hidden while cold-load has failed — the filter chips it holds shouldn't stay
        // reachable/removable once the underlying data (and the filter panel itself) is gone.
        mobileAppliedHost.hidden = coldLoadFailed || appliedWrap.hidden;
        return;
      }

      if (appliedWrapOriginalParent && appliedWrap.parentElement !== appliedWrapOriginalParent) {
        if (appliedWrapOriginalNextSibling && appliedWrapOriginalNextSibling.parentElement === appliedWrapOriginalParent) {
          appliedWrapOriginalParent.insertBefore(appliedWrap, appliedWrapOriginalNextSibling);
        } else {
          appliedWrapOriginalParent.appendChild(appliedWrap);
        }
      }
      if (clearAllBtnOriginalParent && clearAllBtn && clearAllBtn.parentElement !== clearAllBtnOriginalParent) {
        if (clearAllBtnOriginalNextSibling && clearAllBtnOriginalNextSibling.parentElement === clearAllBtnOriginalParent) {
          clearAllBtnOriginalParent.insertBefore(clearAllBtn, clearAllBtnOriginalNextSibling);
        } else {
          clearAllBtnOriginalParent.appendChild(clearAllBtn);
        }
      }
      mobileAppliedHost.hidden = true;
    }

    function captureUnfilteredSnapshot() {
      const resultsGrid = document.querySelector('.main-products-grid__results .nu-products-table__body')
        || document.querySelector('.main-products-grid__results > ul');
      if (!resultsGrid) return;
      unfilteredGridHtml = resultsGrid.innerHTML;
      if (paginationWrap) {
        originalPaginationHtml = paginationWrap.innerHTML;
      }
    }

    // Scenario B/C (cold-load timeout / data unavailable): the SKU search box and the
    // rows-per-page select both end up calling applySelections(), which needs the same
    // productMap/renderedProducts data that just failed to load — hide them entirely
    // (rather than disable) so there's nothing on screen implying they might still work.
    function setToolbarControlsHidden(isHidden) {
      if (toolbarSkuSearchForm) toolbarSkuSearchForm.hidden = isHidden;
      if (toolbarRowsControl) toolbarRowsControl.hidden = isHidden;
      // Without the search form, "Total N items" and "Sort by" collapse onto the grid's
      // two separate rows (search/controls) — this modifier puts them on one row instead.
      if (productsToolbarEl) productsToolbarEl.classList.toggle('nu-products-toolbar--no-search', isHidden);
    }

    // On mobile, the filters normally live in an off-canvas drawer that only opens when
    // the "Filters" button is tapped. If cold load has already failed, that button and
    // the closed drawer just delay the error by one extra tap — force the drawer to render
    // inline instead (CSS only changes anything within the mobile breakpoint) and hide the
    // button that would otherwise open an (empty, erroring) drawer.
    function setMobileFiltersErrorLayout(isError) {
      if (mobileFiltersWrap) mobileFiltersWrap.classList.toggle('nu-mobile-filters--cold-load-error', isError);
      if (mobileFiltersOpenBtn) mobileFiltersOpenBtn.hidden = isError;
      // Re-sync immediately rather than waiting for the next resize/selection-change —
      // coldLoadFailed just changed, and syncAppliedFiltersPlacement() is what actually
      // hides/shows .nu-mobile-applied-filters based on it.
      syncAppliedFiltersPlacement();
    }

    // Scenario B/C: cold-load data never hydrated. Replace the filter groups with an
    // error message + retry button, and fall back to the plain server-rendered pagination
    // (real <a href> links, full page reload) instead of the client-side one, since the
    // latter depends on the same data that just failed to load.
    function showColdLoadError(kind) {
      coldLoadFailed = true;
      if (specFiltersBody) specFiltersBody.hidden = true;
      const wrap = ensureColdLoadErrorMounted();
      if (wrap) {
        wrap.hidden = false;
        coldLoadErrorPanels.forEach((panel) => {
          panel.hidden = panel.dataset.nuErrorPanel !== kind;
        });
      }
      setToolbarControlsHidden(true);
      setMobileFiltersErrorLayout(true);
      if (paginationWrap && originalPaginationHtml) {
        paginationWrap.innerHTML = originalPaginationHtml;
        paginationWrap.hidden = false;
      }
      setGroupsLoading(false);
      setProductsTableLoading(false);
    }

    function clearColdLoadError() {
      if (!coldLoadFailed) return;
      coldLoadFailed = false;
      if (coldLoadErrorWrap) coldLoadErrorWrap.hidden = true;
      if (specFiltersBody) specFiltersBody.hidden = false;
      setToolbarControlsHidden(false);
      setMobileFiltersErrorLayout(false);
    }

    // Shared by scenario A (filter/search produced 0 results) and scenario D (SSR/JS
    // render mismatch or an exception while parsing/rendering): both replace the results
    // area with a message and hide the product table and its footer. Note:
    // .nu-products-table__footer is a sibling of .nu-products-table in the DOM, not a
    // child — hiding the table alone does not hide the footer, so it's addressed separately.
    // Panel is toggled first — a simple boolean property set that is extremely unlikely to
    // fail — so the error message is visible before anything else is touched, in case a
    // later line throws for some unexpected reason.
    //
    // The two scenarios differ on the toolbar: scenario D hides it entirely — the render
    // pipeline itself is broken, so nothing in it (search included) is trustworthy. Scenario
    // A only hides "Total N items" (now inaccurate) and the sort control; the SKU search
    // form stays visible and operable since it's the only way for the user to clear or
    // change the search that produced the empty result — see keepSearchVisible.
    function setResultsErrorState(panelEl, isActive, keepSearchVisible) {
      if (panelEl) panelEl.hidden = !isActive;
      if (productsTable) productsTable.hidden = isActive;
      if (tableFooter) tableFooter.hidden = isActive;
      if (keepSearchVisible) {
        if (toolbarTotalEl) toolbarTotalEl.hidden = isActive;
        if (toolbarSortControlsEl) toolbarSortControlsEl.hidden = isActive;
      } else if (productsToolbarEl) {
        productsToolbarEl.hidden = isActive;
      }
    }

    // Scenario D: filter options are fine (this only runs once cold load has already
    // succeeded), but the result of a filter/search/page action doesn't match what was
    // expected — either an exception while parsing/rendering it, or Liquid's SSR count
    // said there should be results and JS ended up with none. Replace the results area
    // (not the filter block — that part isn't what's broken) with an error + reload button;
    // reload is deliberately a full page refresh rather than a partial retry, since the
    // client-side state (productMap, renderedProducts) may itself be out of sync by now.
    function showRenderMismatchError() {
      setResultsErrorState(ensureRenderErrorPanelMounted(), true);
    }

    function clearRenderMismatchError() {
      // Never mounted means it was never shown — nothing to clear, and mounting it here
      // just to immediately hide it again would defeat the point of the lazy template.
      if (!renderErrorPanel) return;
      setResultsErrorState(renderErrorPanel, false);
    }

    // Scenario A: filter / SKU search produced 0 results.
    // Hide the product table, footer, and toolbar — same treatment as scenario D.
    function showEmptyState() {
      setResultsErrorState(ensureEmptyNoticeMounted(), true, true);
    }

    function clearEmptyState() {
      // Never mounted means it was never shown — nothing to clear, and mounting it here
      // just to immediately hide it again would defeat the point of the lazy template.
      if (!emptyNotice) return;
      setResultsErrorState(emptyNotice, false, true);
    }

    function getRowsPerPageFromUrl() {
      if (nuFiltersLite?.getPositiveIntParam) {
        return nuFiltersLite.getPositiveIntParam('nu_rows', 25);
      }
      const params = new URLSearchParams(window.location.search);
      const parsed = Number(params.get('nu_rows') || '25');
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 25;
    }

    function buildPageUrl(page) {
      const url = new URL(window.location.href);
      if (page <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', String(page));
      }
      return url.toString();
    }

    function getStickyHeaderOffset() {
      let maxBottom = 0;
      const candidates = Array.from(document.querySelectorAll(
        'store-header, #shopify-section-header, .shopify-section-header-sticky, [data-is-sticky="true"], header'
      ));
      candidates.forEach((element) => {
        if (!element || !document.body.contains(element)) return;
        const styles = window.getComputedStyle(element);
        if (styles.display === 'none' || styles.visibility === 'hidden') return;
        if (styles.position !== 'fixed' && styles.position !== 'sticky') return;
        const rect = element.getBoundingClientRect();
        if (rect.height <= 0) return;
        if (rect.top <= 4 && rect.bottom > 0) {
          maxBottom = Math.max(maxBottom, rect.bottom);
        }
      });
      const rootStyles = window.getComputedStyle(document.documentElement);
      const cssHeaderHeight = Number.parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;
      const cssStickyHeaderHeight = Number.parseFloat(rootStyles.getPropertyValue('--sticky-header-height')) || 0;
      const cssOffset = Math.max(cssHeaderHeight, cssStickyHeaderHeight);
      return Math.max(0, Math.round(Math.max(maxBottom, cssOffset)));
    }

    function scrollToProductsTotal() {
      const totalEl = document.querySelector('[data-nu-products-total]');
      if (!totalEl) return;
      const doScroll = () => {
        const stickyOffset = getStickyHeaderOffset();
        const extraGap = 28;
        const targetTop = window.scrollY + totalEl.getBoundingClientRect().top - stickyOffset - extraGap;
        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth',
        });
      };
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(doScroll);
      });
    }

    // Shared with nu--products-toolbar.js via NuFiltersLite.getPaginationPageItems() (see
    // nu--filters-lite.js) so the two never diverge from each other or from
    // snippets/nu--pagination.liquid's start_page/end_page logic.
    function getPaginationPageItems(currentPage, totalPages) {
      return nuFiltersLite?.getPaginationPageItems(currentPage, totalPages) ?? [];
    }

    // Link text / aria-label for a client-built pagination skeleton. The translations are
    // already rendered (and escaped) onto <products-toolbar> as data attributes for
    // nu--products-toolbar.js's own client-rendered pagination, so reuse them instead of
    // adding a second set.
    function getPaginationLabels() {
      const data = document.querySelector('products-toolbar')?.dataset || {};
      return {
        wrap: data.paginationLabel || 'Pagination',
        first: data.paginationFirstPageLabel || 'First page',
        prev: data.paginationPrevious || 'Previous',
        next: data.paginationNext || 'Next',
        last: data.paginationLastPageLabel || 'Last page',
      };
    }

    function renderClientPagination(paginationWrap, totalItems, currentPageNum, rowsPerPage) {
      if (!paginationWrap) return;
      const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
      const safeCurrentPage = Math.min(Math.max(1, currentPageNum), totalPages);

      if (totalPages <= 1) {
        paginationWrap.hidden = true;
        return;
      }

      if (!paginationWrap.querySelector('.js-pagination')) {
        if (originalPaginationHtml) {
          paginationWrap.innerHTML = originalPaginationHtml;
        } else {
          // No server-rendered pagination to clone (single-page collection, H-40) — build the
          // same skeleton snippets/nu--pagination.liquid would have rendered.
          const skeleton = nuFiltersLite?.buildPaginationSkeleton?.(getPaginationLabels());
          if (skeleton) {
            paginationWrap.innerHTML = '';
            paginationWrap.appendChild(skeleton);
          }
        }
      }

      const listEl = paginationWrap.querySelector('.js-pagination');
      if (!listEl) {
        paginationWrap.hidden = true;
        return;
      }

      const setArrowState = (arrowEl, enabled, targetPage) => {
        if (!arrowEl) return;
        const itemEl = arrowEl.closest('.pagination__item');
        if (itemEl) itemEl.hidden = !enabled;
        arrowEl.classList.add('nu-client-page-link');
        if (enabled) {
          arrowEl.setAttribute('href', buildPageUrl(targetPage));
          arrowEl.removeAttribute('role');
          arrowEl.removeAttribute('aria-disabled');
          arrowEl.setAttribute('data-nu-client-page', String(targetPage));
        } else {
          arrowEl.removeAttribute('href');
          arrowEl.setAttribute('role', 'link');
          arrowEl.setAttribute('aria-disabled', 'true');
          arrowEl.removeAttribute('data-nu-client-page');
        }
      };

      const prevPage = safeCurrentPage - 1;
      const nextPage = safeCurrentPage + 1;
      const hasPrev = safeCurrentPage > 1;
      const hasNext = safeCurrentPage < totalPages;
      const hasFirst = safeCurrentPage > 1;
      const hasLast = safeCurrentPage < totalPages;
      setArrowState(listEl.querySelector('.pagination__arrow--first'), hasFirst, 1);
      setArrowState(listEl.querySelector('.pagination__arrow--prev'), hasPrev, prevPage);
      setArrowState(listEl.querySelector('.pagination__arrow--next'), hasNext, nextPage);
      setArrowState(listEl.querySelector('.pagination__arrow--last'), hasLast, totalPages);

      const mobilePositionEl = listEl.querySelector('.pagination__item.text-center.md\\:hidden.font-bold');
      if (mobilePositionEl) {
        mobilePositionEl.textContent = `${safeCurrentPage} / ${totalPages}`;
      }

      listEl.querySelectorAll('li.hidden.md\\:block').forEach((itemEl) => itemEl.remove());
      const nextArrowItem = listEl.querySelector('.pagination__arrow--next')?.closest('.pagination__item');
      if (nextArrowItem) {
        for (const pageItemValue of getPaginationPageItems(safeCurrentPage, totalPages)) {
          const pageItem = document.createElement('li');
          pageItem.className = 'hidden md:block nu-client-page-item';
          if (pageItemValue === safeCurrentPage) {
            const currentEl = document.createElement('span');
            currentEl.className = 'pagination__page-current font-bold block leading-none';
            currentEl.textContent = String(pageItemValue);
            pageItem.appendChild(currentEl);
          } else {
            const linkEl = document.createElement('a');
            linkEl.className = 'pagination__page-link text-current block leading-none nu-client-page-link';
            linkEl.href = buildPageUrl(pageItemValue);
            linkEl.textContent = String(pageItemValue);
            linkEl.setAttribute('data-nu-client-page', String(pageItemValue));
            pageItem.appendChild(linkEl);
          }
          listEl.insertBefore(pageItem, nextArrowItem);
        }
      }

      if (listEl.dataset.nuClientPaginationBound !== 'true') {
        listEl.dataset.nuClientPaginationBound = 'true';
        listEl.addEventListener('click', (event) => {
          const linkEl = event.target.closest('[data-nu-client-page]');
          if (!linkEl) return;
          event.preventDefault();
          const targetPage = Number(linkEl.getAttribute('data-nu-client-page'));
          if (!Number.isFinite(targetPage) || targetPage < 1) return;
          const url = new URL(window.location.href);
          if (targetPage <= 1) {
            url.searchParams.delete('page');
          } else {
            url.searchParams.set('page', String(targetPage));
          }
          history.replaceState({}, '', url.toString());
          applySelections();
          scrollToProductsTotal();
        });
      }

      paginationWrap.hidden = false;
    }

    function isProductMatchedBySelections(productFilters, selections) {
      let matched = true;
      selections.forEach((item) => {
        if (!matched || item.selected.size === 0) return;
        const hasMatch = hasComparableMatch(productFilters[item.filterKey] || [], item.selected);
        if (!hasMatch) matched = false;
      });
      return matched;
    }

    function syncSelectedRowClasses() {
      groups.forEach((groupEl) => {
        const selectEl = groupEl.querySelector('[data-nu-spec-select]');
        const rowEls = groupEl.querySelectorAll('[data-nu-spec-ms-row]');
        if (!selectEl || rowEls.length === 0) return;
        rowEls.forEach((rowEl) => {
          const index = Number(rowEl.getAttribute('data-index'));
          if (!Number.isFinite(index) || index < 0) return;
          const optionEl = selectEl.options[index];
          if (!optionEl) return;
          const isSelected = Boolean(optionEl.selected);
          const isDisabled = Boolean(optionEl.disabled);
          rowEl.classList.toggle('is-selected', isSelected);
          rowEl.classList.toggle('is-disabled', isDisabled);
          rowEl.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          rowEl.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        });
      });
    }

    function recomputeLinkedOptionDisabledStates() {
      const selections = getSelections();
      const hasAnySelection = selections.some((item) => item.selected.size > 0);

      groups.forEach((targetGroup) => {
        const isCheckboxControl = getGroupControlType(targetGroup) === 'checkbox';
        const targetSelect = targetGroup.querySelector('[data-nu-spec-select]');
        const targetCheckboxes = targetGroup.querySelectorAll('[data-nu-spec-checkbox]');
        if (!targetSelect && targetCheckboxes.length === 0) return;

        if (!hasAnySelection) {
          if (isCheckboxControl) {
            targetCheckboxes.forEach((checkbox) => {
              checkbox.disabled = false;
            });
          } else {
            Array.from(targetSelect.options).forEach((option) => {
              option.disabled = false;
            });
          }
          return;
        }

        const targetFilterKey = targetGroup.dataset.filterKey;
        const otherSelections = selections.filter((item) => (
          item.filterKey !== targetFilterKey && item.selected.size > 0
        ));

        const controls = isCheckboxControl
          ? Array.from(targetCheckboxes).map((checkbox) => ({
            value: checkbox.value,
            isSelected: checkbox.checked,
            setDisabled: (disabled) => {
              checkbox.disabled = disabled;
            },
          }))
          : Array.from(targetSelect.options).map((option) => ({
            value: option.value,
            isSelected: option.selected,
            setDisabled: (disabled) => {
              option.disabled = disabled;
            },
          }));

        controls.forEach((control) => {
          const optionValue = normalizeValue(control.value);
          if (!optionValue) {
            control.setDisabled(true);
            return;
          }

          // Keep already-selected values interactive so user can unselect them.
          if (control.isSelected) {
            control.setDisabled(false);
            return;
          }

          let hasResult = false;
          Object.values(productMap).some((productFilters) => {
            if (!isProductMatchedBySelections(productFilters, otherSelections)) return false;
            if (hasComparableMatch(productFilters[targetFilterKey] || [], new Set([optionValue]))) {
              hasResult = true;
              return true;
            }
            return false;
          });

          control.setDisabled(!hasResult);
        });
      });

      syncSelectedRowClasses();
    }

    function updateLinkedOptionDisabledStates() {
      // Reflect the option that was just clicked immediately, so its own selected/unselected
      // style doesn't wait on the (potentially expensive, O(groups x options x products))
      // disabled-state recompute below. That recompute only affects OTHER options anyway, so
      // it's safe to defer it.
      // Note: requestAnimationFrame runs BEFORE the next paint, not after — scheduling the
      // heavy work there would still bundle it into the same paint as the change above,
      // defeating the point. setTimeout hands control back to the event loop's macrotask
      // queue, which lets the browser paint the pending change first.
      syncSelectedRowClasses();

      if (disabledRecomputeScheduled) return;
      disabledRecomputeScheduled = true;
      window.setTimeout(() => {
        disabledRecomputeScheduled = false;
        recomputeLinkedOptionDisabledStates();
      });
    }

    function getCurrentPageFromUrl() {
      if (nuFiltersLite?.getPositiveIntParam) {
        return nuFiltersLite.getPositiveIntParam('page', 1);
      }
      const params = new URLSearchParams(window.location.search);
      const parsed = Number(params.get('page') || 1);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    async function refreshRenderedProductsForCurrentUrl() {
      const fetchToken = renderedProductsFetchToken + 1;
      renderedProductsFetchToken = fetchToken;
      // A newer call superseding this one should stop this one's requests from continuing
      // to occupy the shared concurrency pool for results nobody will use.
      if (renderedProductsFetchController) renderedProductsFetchController.abort();
      const controller = new AbortController();
      renderedProductsFetchController = controller;
      const pageFromUrl = getCurrentPageFromUrl();

      const allRenderedProducts = await fetchAllPagesRenderedProducts({
        sectionId,
        totalPages,
        currentPage: pageFromUrl,
        debugEnabled,
        signal: controller.signal,
      });

      if (renderedProductsFetchController === controller) renderedProductsFetchController = null;

      if (renderedProductsFetchToken !== fetchToken) return;

      if (!allRenderedProducts || allRenderedProducts.orderedHandles.length === 0) {
        // A total fetch failure shouldn't discard whatever cross-page data was already
        // gathered by a prior successful call — merge like every other update path here does,
        // instead of replacing renderedProducts with just the current single page.
        renderedProducts = mergeRenderedProducts(renderedProducts, extractGridItemsFromDom(document));
        return;
      }

      renderedProducts = allRenderedProducts;
      writeRenderedProductsCache(renderedProductsCacheKey, renderedProducts);
      nuSpecDebug(debugEnabled, 'rendered products merged', {
        pageFromUrl,
        products: renderedProducts.orderedHandles.length,
      });
    }

    function refreshSelectionUi() {
      groups.forEach((group) => {
        updateGroupClearButton(group);
      });
      updateAppliedFiltersSummary();
      updateClearAllButton();
      updateLinkedOptionDisabledStates();
    }

    function collectSelectionDiagnostics(selections) {
      if (!debugEnabled) return [];

      return selections
        .filter((item) => item.selected.size > 0)
        .map((item) => {
          let productsWithFilterKey = 0;
          let productsWithAnyValue = 0;
          const sampleValues = [];

          Object.values(productMap || {}).forEach((productFilters) => {
            if (!productFilters || typeof productFilters !== 'object') return;
            if (!(item.filterKey in productFilters)) return;
            productsWithFilterKey += 1;
            const normalizedValues = toNormalizedDisplayValues(productFilters[item.filterKey] || []);
            if (normalizedValues.length > 0) {
              productsWithAnyValue += 1;
              if (sampleValues.length < 10) {
                sampleValues.push(...normalizedValues.slice(0, 10 - sampleValues.length));
              }
            }
          });

          return {
            param: item.param,
            filterKey: item.filterKey,
            selected: Array.from(item.selected),
            productsWithFilterKey,
            productsWithAnyValue,
            sampleValues,
          };
        });
    }

    async function applySelections({ busyOverlay = false } = {}) {
      // Apply, Clear All, chip removal, SKU search, pagination clicks and rows-per-page
      // changes all funnel through here, and this function has several `await` points —
      // without this, a slower earlier call can resolve after a faster later one and
      // overwrite its DOM writes with stale data. Every await below is followed by a
      // token check that bails out (via the try/finally) the moment a newer call has
      // started, so only the most recent call's writes ever land.
      const applySelectionsCallToken = ++applySelectionsToken;
      beginProductsTableRenderLoading();
      const layoutRoot = container.closest('.nu--collection-layout');
      const shouldToggleGlobalBusy = Boolean(busyOverlay && layoutRoot);
      if (shouldToggleGlobalBusy) {
        layoutRoot.setAttribute('data-nu-busy', 'true');
        document.body.classList.add('nu-global-refreshing');
      }
      try {
      await waitForNextFrame();
      if (applySelectionsCallToken !== applySelectionsToken) return;
      clearRenderMismatchError();
      clearEmptyState();
      const selections = getSelections();
      appliedFiltersExpanded = false;
      appliedSelectionsByParam = new Map(
        selections
          .filter((item) => item.selected.size > 0)
          .map((item) => [item.param, new Set(item.selected)])
      );
      appliedSelectionCount = selections.reduce((sum, item) => sum + item.selected.size, 0);
      updateUrl(selections);
      refreshSelectionUi();

      const hasActiveSelection = selections.some((item) => item.selected.size > 0);
      const hasActiveSkuQuery = currentSkuQuery.length > 0;
      const hasActiveFiltering = hasActiveSelection || hasActiveSkuQuery;
      if (hasActiveFiltering) {
        await ensureProductMapHydratedForFiltering();
        if (applySelectionsCallToken !== applySelectionsToken) return;
        // Cold load just failed (scenario B/C) — showColdLoadError() already replaced
        // the filter block with the error UI. Leave the currently-rendered SSR page
        // alone rather than filtering it against the incomplete fallback map.
        if (coldLoadFailed) return;
      }
      const currentCards = Array.from(document.querySelectorAll('.main-products-grid__results [data-product-handle]'));
      const resultsGrid = document.querySelector('.main-products-grid__results .nu-products-table__body')
        || document.querySelector('.main-products-grid__results > ul');
      const rowsPerPage = getRowsPerPageFromUrl();
      const currentPageNumber = getCurrentPageFromUrl();
      const currentRenderedProducts = extractGridItemsFromDom(document);
      renderedProducts = mergeRenderedProducts(renderedProducts, currentRenderedProducts);
      let visibleCountOnPage = 0;
      let totalMatchedCount = 0;

      const matchedHandles = [];
      if (hasActiveFiltering) {
        Object.entries(productMap).forEach(([handle, productFilters]) => {
          let isMatch = true;
          selections.forEach((item) => {
            if (!isMatch || item.selected.size === 0) return;
            const hasMatch = hasComparableMatch(productFilters[item.filterKey] || [], item.selected);
            if (!hasMatch) isMatch = false;
          });
          if (isMatch && hasActiveSkuQuery) {
            const skus = renderedProducts.handleToSkus?.[handle] || [];
            const skuMatched = skus.some((sku) => sku.includes(currentSkuQuery));
            if (!skuMatched) isMatch = false;
          }
          if (isMatch) matchedHandles.push(handle);
        });
        totalMatchedCount = matchedHandles.length;
      }

      nuSpecDebug(debugEnabled, 'apply selection matched handles', {
        hasActiveSelection,
        matchedCount: matchedHandles.length,
        renderedCount: renderedProducts.orderedHandles.length,
      });
      if (hasActiveSelection) {
        const selectionDiagnostics = collectSelectionDiagnostics(selections);
        nuSpecDebug(debugEnabled, 'selection diagnostics', selectionDiagnostics);
        if (selectionDiagnostics.length > 0) {
          nuSpecDebug(debugEnabled, 'selection diagnostics json', JSON.stringify(selectionDiagnostics));
        }
      }

      if (!hasActiveFiltering) {
        // Cold-load failure with no filters active (e.g. the last one was just cleared)
        // never routes through the `if (hasActiveFiltering)` guard above, so it needs its
        // own check here: showColdLoadError() already restored the plain server-rendered
        // pagination (real <a href> links) because client-side pagination depends on the
        // same data that just failed to load — rebuilding it below would undo that.
        if (coldLoadFailed) return;
        if (resultsGrid && unfilteredGridHtml) {
          resultsGrid.innerHTML = unfilteredGridHtml;
        }
        const totalItemsForPagination = Number.isFinite(baseResultsCount) && baseResultsCount > 0
          ? baseResultsCount
          : renderedProducts.orderedHandles.length;
        const totalPagesForPagination = Math.max(1, Math.ceil(totalItemsForPagination / rowsPerPage));
        const safeCurrentPageForPagination = Math.min(Math.max(1, currentPageNumber), totalPagesForPagination);
        const requiredEndIndex = rowsPerPage >= totalItemsForPagination
          ? totalItemsForPagination
          : Math.min(totalItemsForPagination, safeCurrentPageForPagination * rowsPerPage);
        if (renderedProducts.orderedHandles.length < requiredEndIndex) {
          await refreshRenderedProductsForCurrentUrl();
          if (applySelectionsCallToken !== applySelectionsToken) return;
        }
        const restoredCards = Array.from(document.querySelectorAll('.main-products-grid__results [data-product-handle]'));
        restoredCards.forEach((card) => {
          card.classList.remove('nu-spec-hidden');
        });

        const totalCards = renderedProducts.orderedHandles.length > 0
          ? renderedProducts.orderedHandles.length
          : restoredCards.length;
        const normalizedTotalItemsForPagination = Number.isFinite(baseResultsCount) && baseResultsCount > 0
          ? baseResultsCount
          : totalCards;
        const needsClientPagination = rowsPerPage > 0 && rowsPerPage < normalizedTotalItemsForPagination;

        if (needsClientPagination) {
          const totalPages = Math.max(1, Math.ceil(normalizedTotalItemsForPagination / rowsPerPage));
          const safeCurrentPage = Math.min(Math.max(1, currentPageNumber), totalPages);
          if (safeCurrentPage !== currentPageNumber) {
            const normalizedUrl = new URL(window.location.href);
            if (safeCurrentPage <= 1) {
              normalizedUrl.searchParams.delete('page');
            } else {
              normalizedUrl.searchParams.set('page', String(safeCurrentPage));
            }
            history.replaceState({}, '', normalizedUrl.toString());
          }
          const startIndex = (safeCurrentPage - 1) * rowsPerPage;
          const endIndex = startIndex + rowsPerPage;
          const pageHandles = renderedProducts.orderedHandles.slice(startIndex, endIndex);
          const pageHtml = pageHandles
            .map((handle) => renderedProducts.handleToHtml[handle])
            .filter(Boolean);
          if (resultsGrid && pageHtml.length > 0) {
            resultsGrid.innerHTML = pageHtml.join('');
            visibleCountOnPage = pageHtml.length;
          } else {
            // Don't have enough cross-page data yet to know exactly which handles belong on
            // this page (e.g. this refresh got superseded by an overlapping later call — see
            // renderedProductsFetchToken in refreshRenderedProductsForCurrentUrl()).
            // restoredCards are whatever the server actually rendered for this exact page/URL,
            // so trust them as-is instead of guessing: startIndex/endIndex are a GLOBAL
            // position across the whole collection, but restoredCards' index is LOCAL to this
            // page, so comparing them here would hide every card on any page beyond the first
            // and spuriously trip scenario D (0 visible, baseResultsCount > 0).
            visibleCountOnPage = restoredCards.length;
          }
          if (paginationWrap) {
            renderClientPagination(paginationWrap, normalizedTotalItemsForPagination, safeCurrentPage, rowsPerPage);
          }
        } else {
          if (resultsGrid && renderedProducts.orderedHandles.length > 0) {
            const allHtml = renderedProducts.orderedHandles
              .map((handle) => renderedProducts.handleToHtml[handle])
              .filter(Boolean);
            resultsGrid.innerHTML = allHtml.join('');
            visibleCountOnPage = allHtml.length;
          } else {
            visibleCountOnPage = totalCards;
          }
          // Everything fits on one page here, so any ?page=N (N > 1) is out of range — e.g.
          // ?page=2 refreshed on a one-page collection, where the server renders the empty
          // page 2 but this code shows the full list. Set the URL to the last page (which
          // is page 1 here), same as the multi-page branches above, instead of leaving a page
          // number that doesn't exist (H-41 follow-up, PM decision).
          if (currentPageNumber > 1) {
            const normalizedUrl = new URL(window.location.href);
            normalizedUrl.searchParams.delete('page');
            history.replaceState({}, '', normalizedUrl.toString());
          }
          if (paginationWrap) {
            if (originalPaginationHtml) {
              paginationWrap.innerHTML = originalPaginationHtml;
            }
            paginationWrap.hidden = true;
          }
        }

        renderedProducts = mergeRenderedProducts(renderedProducts, extractGridItemsFromDom(document));
      } else if (resultsGrid && renderedProducts.orderedHandles.length > 0) {
        const matchedSet = new Set(matchedHandles);
        let matchedHtml = renderedProducts.orderedHandles
          .filter((handle) => matchedSet.has(handle))
          .map((handle) => renderedProducts.handleToHtml[handle])
          .filter(Boolean);

        if (matchedHandles.length > 0 && matchedHtml.length < matchedHandles.length) {
          await refreshRenderedProductsForCurrentUrl();
          if (applySelectionsCallToken !== applySelectionsToken) return;
          matchedHtml = renderedProducts.orderedHandles
            .filter((handle) => matchedSet.has(handle))
            .map((handle) => renderedProducts.handleToHtml[handle])
            .filter(Boolean);

          if (matchedHtml.length < matchedHandles.length) {
            await refreshRenderedProductsForCurrentUrl();
            if (applySelectionsCallToken !== applySelectionsToken) return;
            matchedHtml = renderedProducts.orderedHandles
              .filter((handle) => matchedSet.has(handle))
              .map((handle) => renderedProducts.handleToHtml[handle])
              .filter(Boolean);
          }

          nuSpecDebug(debugEnabled, 'recheck matched html after refresh', {
            matchedCount: matchedHandles.length,
            matchedHtmlCount: matchedHtml.length,
            renderedCount: renderedProducts.orderedHandles.length,
          });
        }

        totalMatchedCount = matchedHtml.length;
        const totalPages = Math.max(1, Math.ceil(totalMatchedCount / rowsPerPage));
        const safeCurrentPage = Math.min(Math.max(1, currentPageNumber), totalPages);
        if (safeCurrentPage !== currentPageNumber) {
          const normalizedUrl = new URL(window.location.href);
          if (safeCurrentPage <= 1) {
            normalizedUrl.searchParams.delete('page');
          } else {
            normalizedUrl.searchParams.set('page', String(safeCurrentPage));
          }
          history.replaceState({}, '', normalizedUrl.toString());
        }
        const startIndex = (safeCurrentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const pagedMatchedHtml = matchedHtml.slice(startIndex, endIndex);

        if (pagedMatchedHtml.length > 0) {
          resultsGrid.innerHTML = pagedMatchedHtml.join('');
          visibleCountOnPage = pagedMatchedHtml.length;
        } else {
          resultsGrid.innerHTML = '';
          visibleCountOnPage = 0;
        }

        if (paginationWrap) {
          renderClientPagination(paginationWrap, totalMatchedCount, safeCurrentPage, rowsPerPage);
        }
      } else {
        const matchedHandleSet = new Set(matchedHandles);
        currentCards.forEach((card) => {
          const productHandle = card.dataset.productHandle;
          const isVisible = matchedHandleSet.has(productHandle);
          card.classList.toggle('nu-spec-hidden', !isVisible);
          if (isVisible) visibleCountOnPage += 1;
        });
        totalMatchedCount = visibleCountOnPage;
      }

      // Scenario D: no filter/search active, Liquid's SSR count says this page should have
      // results, but nothing ended up rendered — a mismatch between the SSR and JS-parsed
      // state, not a genuinely empty collection (that's covered by the products_count > 0
      // gate at the section level, so baseResultsCount > 0 here is a real signal).
      if (!hasActiveFiltering && !coldLoadFailed && Number.isFinite(baseResultsCount) && baseResultsCount > 0 && visibleCountOnPage === 0) {
        showRenderMismatchError();
        return;
      }

      // Scenario A: active filter/search but nothing to show — see setResultsErrorState()
      // for exactly what gets hidden/restored (table, footer, total count, sort control;
      // the SKU search form stays visible/operable).
      if (hasActiveFiltering && visibleCountOnPage === 0) {
        showEmptyState();
      } else {
        clearEmptyState();
      }

      const totalCount = hasActiveFiltering
        ? totalMatchedCount
        : (Number.isFinite(baseResultsCount) && baseResultsCount > 0
          ? baseResultsCount
          : renderedProducts.orderedHandles.length);
      document.dispatchEvent(new CustomEvent('nu:products-count:update', {
        detail: { count: totalCount },
      }));
      document.dispatchEvent(new CustomEvent('nu:r4q:refresh', {
        detail: {
          busy: busyOverlay,
          forceVisible: busyOverlay,
          version: nuFiltersLite?.nextR4QRefreshVersion() ?? (r4qRefreshVersionFallbackCounter += 1),
        },
      }));

      setPendingChanges(false);
      } catch (error) {
        nuSpecDebug(debugEnabled, 'applySelections failed', error?.message || error);
        // Cold-load failures (scenario B/C) already have their own error UI — don't
        // layer scenario D's on top if this exception is really just fallout from that.
        if (!coldLoadFailed) {
          showRenderMismatchError();
        }
      } finally {
        // Only the call that's still current is allowed to clear the busy overlay — a
        // superseded call reaching here (via one of the token-check `return`s above) must
        // not clear a state that a newer, still-in-flight call already owns. Whether *this*
        // call is the one that set the overlay (shouldToggleGlobalBusy) is irrelevant here:
        // some other call may have set it, and removeAttribute/classList.remove are no-ops
        // if there's nothing to clear, so it's safe to always try once we're the latest call.
        if (layoutRoot && applySelectionsCallToken === applySelectionsToken) {
          layoutRoot.removeAttribute('data-nu-busy');
          document.body.classList.remove('nu-global-refreshing');
        }
        endProductsTableRenderLoading();
      }
    }

    function restoreFromUrl() {
      currentSkuQuery = getSkuQueryFromUrl();
      urlSelections = parseSelectionsFromUrl(groups);
      const hasUrlSelections = Array.from(urlSelections.values()).some((values) => values.size > 0);
      const restoreSelections = hasUrlSelections
        ? cloneSelectionsMap(urlSelections)
        : cloneSelectionsMap(appliedSelectionsByParam);

      groups.forEach((group) => {
        const param = group.dataset.param;
        const values = restoreSelections.get(param);
        if (!values) return;
        if (getGroupControlType(group) === 'checkbox') {
          group.querySelectorAll('[data-nu-spec-checkbox]').forEach((checkbox) => {
            checkbox.checked = values.has(normalizeValue(checkbox.value));
          });
        } else {
          const select = group.querySelector('[data-nu-spec-select]');
          if (!select) return;
          Array.from(select.options).forEach((option) => {
            option.selected = values.has(normalizeValue(option.value));
          });
        }
      });

      urlSelections = restoreSelections;
      appliedSelectionsByParam = cloneSelectionsMap(restoreSelections);
    }

    function rebuildOptionsFromProductMap(sourceMap) {
      const filterKeys = groups
        .map((group) => group.dataset.filterKey)
        .filter((key) => Boolean(key));
      const optionsByFilterKey = buildOptionsByFilterKey(sourceMap, filterKeys);

      groups.forEach((group) => {
        const filterKey = group.dataset.filterKey;
        const param = group.dataset.param;
        const checkedValues = getSelectedValues(group);
        const keepValues = new Set([
          ...(urlSelections.get(param) || []),
          ...checkedValues,
        ]);
        renderGroupOptions(group, optionsByFilterKey[filterKey] || [], keepValues);
        updateGroupSearchVisibility(group, searchThreshold);
        applyGroupSearchFilter(group);
        updateGroupMobileOptionLimit(group, maxFilterOptions, isMobileFilterViewport());
      });

      nuSpecDebug(debugEnabled, 'options rebuilt', {
        byFilterKey: Object.fromEntries(
          Object.entries(optionsByFilterKey).map(([key, values]) => [key, values.length])
        ),
      });
    }

    groups.forEach((group) => {
      if (getGroupControlType(group) === 'checkbox') {
        ensureGroupSearchWrap(group);
      }
      updateGroupSearchVisibility(group, searchThreshold);
      updateGroupMobileOptionLimit(group, maxFilterOptions, isMobileFilterViewport());
      group.addEventListener('change', (event) => {
        if (event.target.matches('[data-nu-spec-select], [data-nu-spec-checkbox]')) {
          refreshSelectionUi();
          setPendingChanges(true);
        }
      });

      group.addEventListener('input', (event) => {
        if (event.target.matches('[data-nu-spec-search]')) {
          applyGroupSearchFilter(group);
          updateGroupMobileOptionLimit(group, maxFilterOptions, isMobileFilterViewport());
        }
      });

      group.addEventListener('click', (event) => {
        const showMoreBtn = event.target.closest('.js-nu-spec-show-more');
        if (!showMoreBtn) return;
        event.preventDefault();
        group.dataset.nuMobileOptionsExpanded = 'true';
        updateGroupMobileOptionLimit(group, maxFilterOptions, isMobileFilterViewport());
      });

      const clearBtn = group.querySelector('[data-nu-spec-clear]');
      if (clearBtn) {
        clearBtn.addEventListener('click', (event) => {
          event.preventDefault();
          if (getGroupControlType(group) === 'checkbox') {
            group.querySelectorAll('[data-nu-spec-checkbox]:checked').forEach((checkbox) => {
              checkbox.checked = false;
            });
          } else {
            const select = group.querySelector('[data-nu-spec-select]');
            if (select) {
              Array.from(select.options).forEach((option) => {
                option.selected = false;
              });
              group.querySelectorAll('[data-nu-spec-ms-row]').forEach((rowEl) => {
                rowEl.classList.remove('is-selected');
                rowEl.setAttribute('aria-selected', 'false');
              });
            }
          }

          const searchInput = group.querySelector('[data-nu-spec-search]');
          if (searchInput) {
            searchInput.value = '';
            applyGroupSearchFilter(group);
            updateGroupMobileOptionLimit(group, maxFilterOptions, isMobileFilterViewport());
          }

          if (getGroupControlType(group) === 'checkbox') {
            group.querySelectorAll('[data-nu-spec-option-row]').forEach((rowEl) => {
              rowEl.hidden = false;
            });
          }
          refreshSelectionUi();
          setPendingChanges(true);
        });
      }
    });

    if (clearAllBtn) {
      clearAllBtn.addEventListener('click', () => {
        groups.forEach((group) => {
          if (getGroupControlType(group) === 'checkbox') {
            group.querySelectorAll('[data-nu-spec-checkbox]:checked').forEach((checkbox) => {
              checkbox.checked = false;
            });
            group.querySelectorAll('[data-nu-spec-option-row]').forEach((rowEl) => {
              rowEl.hidden = false;
            });
          } else {
            const select = group.querySelector('[data-nu-spec-select]');
            if (select) {
              Array.from(select.options).forEach((option) => {
                option.selected = false;
                option.hidden = false;
              });
              group.querySelectorAll('[data-nu-spec-ms-row]').forEach((rowEl) => {
                rowEl.classList.remove('is-selected');
                rowEl.setAttribute('aria-selected', 'false');
                rowEl.hidden = false;
              });
            }
          }

          const searchInput = group.querySelector('[data-nu-spec-search]');
          if (searchInput) {
            searchInput.value = '';
          }
        });
        applySelections({ busyOverlay: true });
      });
    }

    if (mobileFilterMql) {
      const handleViewportChange = () => {
        groups.forEach((group) => {
          updateGroupMobileOptionLimit(group, maxFilterOptions, isMobileFilterViewport());
        });
      };

      if (typeof mobileFilterMql.addEventListener === 'function') {
        mobileFilterMql.addEventListener('change', handleViewportChange);
      } else if (typeof mobileFilterMql.addListener === 'function') {
        mobileFilterMql.addListener(handleViewportChange);
      }
    }

    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        // Disable for the duration of the request so a slow fetch can't be double-submitted;
        // applySelections() calls setPendingChanges(false) on success, which re-disables it
        // via the normal path, so the .finally() only needs to cover the non-success case.
        applyBtn.disabled = true;
        applySelections({ busyOverlay: true }).finally(() => {
          applyBtn.disabled = !hasPendingChanges;
        });
      });
    }

    document.addEventListener('nu:sku-search:update', (event) => {
      if (coldLoadFailed) return;
      const nextQuery = String(event?.detail?.query || '').trim().toLowerCase();
      const shouldShowBusy = Boolean(event?.detail?.busy);
      if (nextQuery === currentSkuQuery) return;
      currentSkuQuery = nextQuery;
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      history.replaceState({}, '', url.toString());
      applySelections({ busyOverlay: shouldShowBusy });
    });

    if (appliedValuesWrap) {
      appliedValuesWrap.addEventListener('click', (event) => {
        const overflowBtn = event.target.closest('[data-nu-spec-applied-overflow]');
        if (overflowBtn) {
          appliedFiltersExpanded = true;
          updateAppliedFiltersSummary();
          return;
        }

        const removeBtn = event.target.closest('[data-nu-spec-applied-remove]');
        if (!removeBtn) return;

        const targetParam = removeBtn.getAttribute('data-param');
        const targetValue = removeBtn.getAttribute('data-value');
        if (!targetParam || !targetValue) return;

        const targetGroup = groups.find((group) => group.dataset.param === targetParam);
        if (!targetGroup) return;

        if (getGroupControlType(targetGroup) === 'checkbox') {
          targetGroup.querySelectorAll('[data-nu-spec-checkbox]').forEach((checkbox) => {
            if (normalizeValue(checkbox.value) === targetValue) {
              checkbox.checked = false;
            }
          });
        } else {
          const select = targetGroup.querySelector('[data-nu-spec-select]');
          if (!select) return;
          Array.from(select.options).forEach((option) => {
            if (normalizeValue(option.value) === targetValue) {
              option.selected = false;
            }
          });
          targetGroup.querySelectorAll('[data-nu-spec-ms-row]').forEach((rowEl) => {
            if (normalizeValue(rowEl.getAttribute('data-value')) === targetValue) {
              rowEl.classList.remove('is-selected');
              rowEl.setAttribute('aria-selected', 'false');
            }
          });
        }

        applySelections({ busyOverlay: true });
      });
    }

    // Lightweight variant: only re-applies current selections without refetching product data.
    // Used when only rowsPerPage changes (nu:rows-per-page:change event).
    container.nuSpecApplyRows = ({ busyOverlay = false } = {}) => {
      if (coldLoadFailed) return Promise.resolve();
      return applySelections({ busyOverlay });
    };

    restoreFromUrl();
    reorderSpecGroupsBySort();
    captureUnfilteredSnapshot();
    if (hasCachedProductMap) {
      rebuildOptionsFromProductMap(productMap);
    }
    let initApplySelectionsCount = 1;
    applySelections({ busyOverlay: false });
    syncAppliedFiltersPlacement();
    window.addEventListener('resize', syncAppliedFiltersPlacement);

    const filterKeys = groups
      .map((group) => group.dataset.filterKey)
      .filter((key) => Boolean(key));
    const storefrontFilterKeys = filterKeys.filter((key) => key !== '__availability');

    function runColdLoadFetch() {
      const asyncLoadTasks = [];
      let shouldReapplyAfterInitialLoads = false;
      setProductsTableLoading(true);

      const storefrontTask = hasCachedProductMap
        ? Promise.resolve().then(() => {
          nuSpecDebug(debugEnabled, 'skip storefront fetch (cached productMap)');
        })
        : fetchSharedStorefrontProductMap()
          .then((storefrontMap) => {
            if (!storefrontMap || Object.keys(storefrontMap).length === 0) return;
            productMap = mergeProductMaps(productMap, storefrontMap);
            writeProductMapCache(productMapCacheKey, productMap);
            nuSpecDebug(debugEnabled, 'storefront merged', summarizeProductMap(productMap, storefrontFilterKeys));
            rebuildOptionsFromProductMap(productMap);
            shouldReapplyAfterInitialLoads = true;
            clearColdLoadError();
          })
          .catch((error) => {
            nuSpecDebug(debugEnabled, 'storefront fetch failed', error?.message || error);
            productMap = fallbackProductMap;
            showColdLoadError(classifyColdLoadErrorKind(error));
          });
      asyncLoadTasks.push(storefrontTask);

      return Promise.allSettled(asyncLoadTasks)
        .then(async () => {
          if (shouldReapplyAfterInitialLoads) {
            initApplySelectionsCount += 1;
            await applySelections({ busyOverlay: false });
          }
        })
        .finally(() => {
          nuSpecDebug(debugEnabled, 'init apply count', {
            count: initApplySelectionsCount,
            reapplyAfterInitialLoads: shouldReapplyAfterInitialLoads,
          });
          setGroupsLoading(false);
          setProductsTableLoading(false);
          document.dispatchEvent(new CustomEvent('nu:spec-filters:ready'));
        });
    }

    // Runs the fetch at most once no matter which caller triggers it first (idle
    // callback on desktop, or the mobile filter drawer's first open).
    function ensureColdLoadFetch() {
      if (!coldLoadPromise) {
        coldLoadPromise = runColdLoadFetch();
      }
      return coldLoadPromise;
    }

    // Exposed so nu--products-toolbar.js can await this (and show its own loading
    // state) before revealing the mobile filter drawer on first open.
    container.nuSpecEnsureDataLoaded = ensureColdLoadFetch;

    // Exposed so nu--products-toolbar.js can re-check this after the await above:
    // the fetch can fail while the drawer open sequence is already in flight.
    container.nuSpecColdLoadFailed = () => coldLoadFailed;

    // Cross-page metafield data isn't needed for first paint (the current page's
    // products are already rendered server-side), so this can wait for the browser
    // to be idle either way. It used to be deferred until the mobile drawer's first
    // open, but that let a cold-load failure surface only after the user tapped
    // "Filters" — the search box would vanish out from under them mid-interaction.
    // Fetching eagerly on mobile too means a failure is already known (and shown,
    // see showColdLoadError()) before the user has a chance to touch anything.
    scheduleIdle(ensureColdLoadFetch);
  }

  function boot() {
    document.querySelectorAll('[data-nu-spec-filters]').forEach((container) => {
      if (container.dataset.nuSpecInitialized === 'true') return;
      initContainer(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // These two listeners are document-level (not scoped inside initContainer(), which already
  // guards itself per-container via container.dataset.nuSpecInitialized), so registering them
  // again on a second execution of this script — e.g. Theme Editor re-injecting this <script>
  // tag after a section settings change — would fire each of them multiple times per event.
  // boot() above must stay unguarded, though: it's what calls initContainer() on any
  // newly-rendered [data-nu-spec-filters] container, and Theme Editor section reloads are
  // exactly when a brand-new container (with no nuSpecInitialized flag yet) shows up.
  if (!window.NuSpecFiltersDocumentListenersBooted) {
    window.NuSpecFiltersDocumentListenersBooted = true;

    // Scenario D's reload button lives in .main-products-grid__results, outside any
    // [data-nu-spec-filters] container, so it needs its own listener rather than one
    // scoped inside initContainer() — a full reload is stateless, no closures needed.
    document.addEventListener('click', (event) => {
      const reloadBtn = event.target.closest('[data-nu-error-action="reload"]');
      if (reloadBtn) location.reload();
    });

    // When the rows-per-page selector changes (toolbar dispatches this instead of reloading),
    // re-run applySelections so the !hasActiveFiltering branch applies the new rowsPerPage.
    document.addEventListener('nu:rows-per-page:change', (event) => {
      const requestId = String(event?.detail?.requestId || '');
      const busyOverlay = Boolean(event?.detail?.busyOverlay);
      const tasks = [];
      document.querySelectorAll('[data-nu-spec-filters]').forEach((container) => {
        if (container.dataset.nuSpecInitialized !== 'true') return;
        if (typeof container.nuSpecApplyRows === 'function') {
          tasks.push(Promise.resolve(container.nuSpecApplyRows({ busyOverlay })));
        }
      });
      Promise.allSettled(tasks).finally(() => {
        document.dispatchEvent(new CustomEvent('nu:rows-per-page:done', {
          detail: { requestId },
        }));
      });
    });
  }
})();

